import React, { useState, useEffect, useRef } from 'react';
import {
    X, ShoppingCart, CreditCard, Building2, Banknote, FileText,
    PlusCircle, Clock, Upload, Loader2, Smartphone,
    IndianRupee, CheckCircle, Ticket, Copy, Check,
} from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { adminOrderService, api, couponService, ocrService } from '../../../services/api'; // eslint-disable-line
import { API_ENDPOINTS } from '../../../config/api';
import { storageEnv } from '../../../config/firebase';
import { AdminCreateOrderRequest, TransactionEntry, Coupon } from '../../../types';
import Snackbar from '../../common/Snackbar';
import { formatRawDateTime } from '../../../utils/format';
import MaskedMobile from '../../common/MaskedMobile';
import BankNameInput from '../../common/BankNameInput';
import CashierPicker from '../../common/CashierPicker';
import Select from '../../common/Select';
import SmartImage from '../../common/SmartImage';
import { ImageSourceTabs, PasteUrlBox } from '../../common/ImageSourceTabs';
import type {
    PaymentMethodType, MultipleSubMode, TransferMode, PendingOrder,
    UploadState, ExtractedPaymentFields, PaymentAutoFillValues, FieldSpec,
} from './paymentFormUtils';
import { DatePicker } from '../../common/DatePicker';
import {
    emptyUpload, TRANSFER_MODES, transferModeLabel,
    extractTextFromExistingImage, uploadToFirebase,
    parseExtractedDateToDdmmyyyy, isValidDdmmyyyy,
    ddmmyyyyToIso, isoToDdmmyyyy,
    fieldsForPaymentType, applyAutoFillValues, clearAutoFilledFields,
    validateUTR, validateChequeNumber, validateAccountHolderName, validateBankName,
} from './paymentFormUtils';

interface CreateOrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    user: { mobile: string; country_code?: string; first_name?: string; last_name?: string } | null;
    adminMobile: string;
    adminName: string;
    adminRole: string;
    initialView?: ModalView;
    preSelectedOrder?: PendingOrder | null;
}

type ModalView = 'list' | 'add_payment' | 'create_order';

// ── File upload tile ───────────────────────────────────────────────────────────
export const FileUpload: React.FC<{
    label: string; required?: boolean; state: UploadState;
    onChange: (file: File) => void; onView?: (url: string, text?: string | null, loading?: boolean) => void; errorMsg?: string;
    onReupload?: () => void;
    // Opens the image preview (with OCR'd extracted details) for an already-uploaded image
    // the moment this tile mounts — used when editing a transaction so the admin lands
    // straight on the proof + details view instead of the plain form first.
    autoView?: boolean;
    // Lets the admin paste an already-hosted image URL instead of picking a file — skips
    // the upload step entirely and seeds the tile with that URL directly.
    onUrlSubmit?: (url: string) => void;
    // Fires when the X (clear) button is pressed, or when switching between the Image
    // Upload / Paste URL tabs — removes whatever was loaded under the previous source.
    onClear?: () => void;
}> = ({ label, required, state, onChange, onView, errorMsg, onReupload, autoView, onUrlSubmit, onClear }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [ocrText, setOcrText] = useState<string | null>(null);
    const [ocrLoading, setOcrLoading] = useState(false);
    // Tracks whether we've already tried running OCR against a pre-existing (not freshly
    // selected) image, so re-clicking View doesn't re-fetch/re-run it every time.
    const triedExistingOcrRef = useRef(false);

    const handleSelect = (f: File) => {
        // Replacing an existing upload — any fields auto-filled from the old image's
        // OCR text are now stale, so clear them rather than leave them mismatched.
        if (state.url) onReupload?.();
        setOcrText(null);
        triedExistingOcrRef.current = false;
        onChange(f);
        if (f.type.startsWith('image/')) {
            setOcrLoading(true);
            ocrService.extractText(f)
                .then(result => setOcrText(result?.text || null))
                .finally(() => setOcrLoading(false));
        }
    };

    const handleUrlChange = async (url: string) => {
        if (state.url) onReupload?.();
        setOcrText(null);
        onUrlSubmit?.(url);
        // Run OCR immediately, same as a freshly picked file — no need to click View first.
        triedExistingOcrRef.current = true;
        setOcrLoading(true);
        const text = await extractTextFromExistingImage(url);
        setOcrText(text);
        setOcrLoading(false);
    };

    // Editing a transaction seeds this tile with an already-uploaded image (no local File
    // object, since nothing was freshly selected) — OCR never ran against it. Fetch it once
    // via the CORS proxy Firebase Storage needs and extract text so View shows the same
    // "Extracted Details" panel it would for a newly selected file.
    const handleView = async () => {
        if (!state.url) return;
        if (state.file || ocrText !== null || ocrLoading || triedExistingOcrRef.current) {
            onView?.(state.url, ocrText, ocrLoading);
            return;
        }
        triedExistingOcrRef.current = true;
        setOcrLoading(true);
        onView?.(state.url, null, true);
        const text = await extractTextFromExistingImage(state.url);
        setOcrText(text);
        onView?.(state.url, text, false);
        setOcrLoading(false);
    };

    // Auto-open the preview whenever this tile has an image — on mount for a tile seeded
    // with an existing image, again the moment a freshly picked file finishes uploading
    // (state.url flips from empty to the hosted URL), and once more if OCR was still running
    // at that point (ocrLoading flips false) — so callers don't need a manual "View" click.
    useEffect(() => {
        if (autoView && state.url) {
            handleView();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoView, state.url, ocrLoading]);

    return (
        <div className="space-y-1">
            <label className="block text-xs font-semibold text-slate-700">
                {label}{required && <span className="text-red-500"> *</span>}
            </label>

            <div>
                <ImageSourceTabs enableUrl={!!onUrlSubmit} onModeChange={() => onClear?.()}>
                    {mode => mode === 'url' ? (
                        <PasteUrlBox url={state.url} onSubmit={handleUrlChange} onClear={onClear} loading={state.uploading} onView={handleView}
                            previewClassName="w-full h-36 object-contain rounded border border-green-200 bg-white" />
                    ) : state.url ? (
                        <div className="w-full flex flex-col gap-2 p-2 rounded-lg border text-sm select-none border-green-300 bg-green-50/50">
                            <SmartImage src={state.url} alt="Thumbnail" className="w-full h-48 object-cover rounded-lg border border-green-200 bg-white cursor-pointer hover:opacity-95 transition-opacity" onClick={handleView} />
                            <span className="text-green-700 text-[11px] truncate" title={state.file?.name || label}>
                                {state.file?.name || label}
                            </span>
                            <div className="flex items-center gap-1.5">
                                <button type="button" onClick={handleView}
                                    className="flex-1 px-2 py-1 text-[10px] font-bold text-[#1B4332] bg-[#1B4332]/10 hover:bg-[#1B4332]/20 rounded transition-colors uppercase tracking-wider">
                                    View
                                </button>
                                <button type="button" onClick={() => inputRef.current?.click()}
                                    className="flex-1 px-2 py-1 text-[10px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors uppercase tracking-wider">
                                    Re-upload
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div
                            onClick={() => !state.uploading && inputRef.current?.click()}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-all select-none ${errorMsg ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-[#1B4332] bg-white'} ${state.uploading ? 'cursor-not-allowed opacity-70' : ''}`}
                        >
                            {state.uploading ? (
                                <><Loader2 size={14} className="text-blue-500 animate-spin shrink-0" /><span className="text-blue-600 text-xs">Uploading... {state.progress}%</span></>
                            ) : (
                                <><Upload size={14} className="text-slate-400 shrink-0" /><span className="text-slate-400 text-xs">Choose file to upload</span></>
                            )}
                        </div>
                    )}
                </ImageSourceTabs>

                {state.error && <p className="text-[10px] text-red-500 mt-1">{state.error}</p>}
                {!state.error && errorMsg && <p className="text-[10px] text-red-500 mt-1">{errorMsg}</p>}
            </div>

            <input ref={inputRef} type="file" accept="image/*" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleSelect(f); e.target.value = ''; }} />
        </div>
    );
};

// ── Best-effort extraction of key payment fields from raw OCR text ─────────────
// UTR/RRN are the authoritative bank-assigned reference and take priority over
// looser labels like "Receipt No" or "Reference No", which some receipts also
// print (often for an internal receipt ID rather than the actual UTR/RRN).
const PRIMARY_ID_LABEL_RE = /(?:UTR|RRN)\b/i;
const SECONDARY_ID_LABEL_RE = /(?:UPI\s*transaction\s*ID|Transaction\s*Reference\s*Number|Transaction\s*ID|Txn\s*ID|Reference\s*Number|Reference\s*No\.?|Ref\s*No\.?|Receipt\s*No\.?)/i;
// Same-line "label: value" form (e.g. "UTR: 412345678901"), tried before the line-by-line
// walk below — cheaper and catches receipts that keep label and value on one line. Bounded
// at 22 chars (the longest real UTR/RRN) but with no floor, since cheque OCR also funnels
// through this same value and cheque numbers can run shorter.
const ID_INLINE_RE = /(?:UTR(?:\s*No\.?)?|RRN|Reference(?:\s*No\.?)?|Transaction(?:\s*ID)?|Txn(?:\s*ID)?)[\s:.-]{1,3}([A-Z0-9]{6,22})\b/i;
const CURRENCY_RE = /(?:₹|Rs\.?|INR)\s*([\d,]+(?:\.\d{1,2})?)/i;
const BARE_AMOUNT_RE = /^([\d,]+\.\d{2})$/;
// Same-line "label: ₹value" form, tried before the line-by-line walk below.
const AMOUNT_INLINE_RE = /(?:Amount|Amt|Paid|Debited|Credited|Transfer\s*Amount)\s*[:-]?\s*(?:₹|Rs\.?|INR)?\s*([\d,]+(?:\.\d{1,2})?)/i;
const MODE_RE = /\b(UPI|IMPS|NEFT|RTGS|Cash Deposit)\b/i;
const BANK_LABEL_RE = /Bank\s*name\b/i;
// Common Indian banks matched by exact name — tried before the generic "...Bank" pattern
// below since it's precise and won't false-positive on a payment app/aggregator name that
// happens to contain the word "Bank". Extend this list as new banks show up in receipts.
const KNOWN_BANK_RE = /(HDFC\s*Bank|ICICI\s*Bank|State\s*Bank\s*of\s*India|SBI|Axis\s*Bank|Kotak\s*Mahindra\s*Bank|Canara\s*Bank|Punjab\s*National\s*Bank|PNB|Bank\s*of\s*Baroda|BOB|Indian\s*Bank|Union\s*Bank|IDFC\s*FIRST\s*Bank|IndusInd\s*Bank|Yes\s*Bank|Federal\s*Bank|AU\s*Small\s*Finance\s*Bank)\b/i;
// No period in the allowed characters — keeps this from spanning across a
// domain/URL fragment like "ank.bank.in" and matching "ank.bank" by mistake.
const BANK_RE = /([A-Za-z][A-Za-z ]{2,30}?Bank)\b/i;
const DATE_RE = /\b(\d{4}[/-]\d{1,2}[/-]\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{1,2}[/-][A-Za-z]{3}[/-]\d{2,4}|\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4})\b/;
const TIME_RE = /\b(\d{1,2}:\d{2}(?::\d{2})?\s?(?:am|pm|AM|PM)?)\b/;

// Walks the OCR text line by line since labels and their values often land on
// separate lines (e.g. "AMOUNT" / "₹" / "3,00,000.00" each on their own line).
function extractPaymentFields(text: string): ExtractedPaymentFields {
    const fields: ExtractedPaymentFields = {};
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

    // Same-line "label: value" hits first — cheapest and most common OCR shape.
    const inlineAmount = text.match(AMOUNT_INLINE_RE);
    if (inlineAmount) fields.amount = `₹${inlineAmount[1]}`;
    const inlineId = text.match(ID_INLINE_RE);
    if (inlineId) fields.transactionId = inlineId[1];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (!fields.amount && /(?:Amount|Amt|Paid|Debited|Credited|Transfer\s*Amount)\b/i.test(line)) {
            for (let j = i; j < Math.min(i + 4, lines.length); j++) {
                const m = lines[j].match(CURRENCY_RE) || lines[j].match(BARE_AMOUNT_RE);
                if (m) { fields.amount = `₹${m[1]}`; break; }
            }
        }
        if (!fields.amount) {
            const m = line.match(CURRENCY_RE);
            if (m) fields.amount = `₹${m[1]}`;
        }

        if (!fields.transferMode) {
            const m = line.match(MODE_RE);
            if (m) {
                const matched = m[1].toUpperCase();
                fields.transferMode = matched === 'CASH DEPOSIT' ? 'CASH_DEPOSIT' : matched;
            }
        }

        // Require an actual date match, not just a bare time — a lone "HH:MM" is too often
        // the phone's status-bar clock (e.g. the "12:49" at the top of a screenshot) rather
        // than the transaction time, and would otherwise lock in before the real date line.
        if (!fields.transactionDateTime) {
            const d = line.match(DATE_RE);
            if (d) {
                const t = line.match(TIME_RE);
                fields.transactionDateTime = [d[1], t?.[1]].filter(Boolean).join(', ');
            }
        }
    }

    // Last-resort amount fallback: a bare Indian comma-grouped decimal anywhere in the text.
    if (!fields.amount) {
        const m = text.match(/\b(\d{1,3}(?:,\d{2,3}){1,}(?:\.\d{2})?)\b/);
        if (m) fields.amount = `₹${m[1]}`;
    }

    // Transaction ID: try the UTR/RRN label first, only falling back to the looser
    // receipt/reference labels if no UTR/RRN is present anywhere in the text.
    const findLabeledValue = (labelRe: RegExp): string | undefined => {
        for (let i = 0; i < lines.length; i++) {
            if (!labelRe.test(lines[i])) continue;
            const inline = lines[i].replace(labelRe, '').replace(/[:\s]/g, '');
            if (/^[A-Za-z0-9]{6,}$/.test(inline)) return inline;
            const next = (lines[i + 1] || '').replace(/\s/g, '');
            if (/^[A-Za-z0-9]{6,}$/.test(next)) return next;
        }
        return undefined;
    };
    if (!fields.transactionId) {
        fields.transactionId = findLabeledValue(PRIMARY_ID_LABEL_RE) || findLabeledValue(SECONDARY_ID_LABEL_RE);
    }

    // Bank name: prefer an explicit "Bank name" label over any incidental "...Bank" phrase —
    // receipts often show the payment app/aggregator name (e.g. "BZ Bank") before the actual
    // beneficiary bank, which is called out later under its own "Bank name" label.
    if (!fields.bankName) {
        const bankLabelIdx = lines.findIndex(l => BANK_LABEL_RE.test(l));
        if (bankLabelIdx !== -1) {
            const inline = lines[bankLabelIdx].replace(BANK_LABEL_RE, '').replace(/[:\s]/g, '');
            fields.bankName = inline || (lines[bankLabelIdx + 1] || '').trim() || undefined;
        }
    }
    // Next, an exact match against the known-bank whitelist — precise, but tried after the
    // explicit label since a receipt can mention an unrelated bank elsewhere (e.g. in a footer).
    if (!fields.bankName) {
        const m = text.match(KNOWN_BANK_RE);
        if (m) fields.bankName = m[1];
    }
    // Last resort: any generic "...Bank" phrase.
    if (!fields.bankName) {
        for (const line of lines) {
            const m = line.match(BANK_RE);
            if (m) { fields.bankName = m[1].trim(); break; }
        }
    }

    return fields;
}

const emptyAutoFillValues = (): PaymentAutoFillValues => ({
    txAmount: '', txDate: '', transferMode: '', paidBy: '',
    utrNumber: '', accountHolderName: '', bankName: '', cashierName: '', cashierPhone: '',
});

// Seeds the editable "Extracted Details" form from whatever OCR was able to find.
// Fields with no reliable OCR signal (Paid By, Cashier details, holder name)
// are left blank for manual entry — that's expected, not a bug.
function seedAutoFillValues(fields: ExtractedPaymentFields): PaymentAutoFillValues {
    const transferMode = (fields.transferMode && (TRANSFER_MODES as string[]).includes(fields.transferMode)) ? fields.transferMode : '';
    return {
        ...emptyAutoFillValues(),
        txAmount: fields.amount ? fields.amount.replace(/[₹,]/g, '').split('.')[0] : '',
        txDate: (fields.transactionDateTime && parseExtractedDateToDdmmyyyy(fields.transactionDateTime)) || '',
        transferMode,
        utrNumber: fields.transactionId || '',
        bankName: fields.bankName || '',
    };
}

export const EditableField: React.FC<{
    label: string; value: string; onChange: (v: string) => void;
    type?: 'text' | 'date' | 'number'; options?: (string | { value: string; label: string })[];
    isBank?: boolean; disabled?: boolean; required?: boolean;
    // Swaps in the cashier roster picker, the same way isBank swaps in
    // BankNameInput. Picking a cashier sets the phone as well, so this variant
    // needs the phone's current value and a two-field change handler; the
    // cashier is still chosen here, against the voucher, and still needs Verify.
    isCashier?: boolean; cashierPhone?: string;
    onCashierChange?: (next: { cashierName: string; cashierPhone: string }) => void;
    onApply?: () => void; justApplied?: boolean; helperText?: string; errorText?: string;
    onFocus?: () => void;
}> = ({ label, value, onChange, type = 'text', options, isBank, isCashier, cashierPhone, onCashierChange, disabled, required, onApply, justApplied, helperText, errorText, onFocus }) => (
    <div className="py-1">
        <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 w-36 shrink-0 truncate" title={label}>
                {label}
                {required === true && <span className="text-red-500"> *</span>}
                {required === false && <span className="text-slate-400 font-normal normal-case"> (optional)</span>}
            </span>
            {isCashier && onCashierChange ? (
                <div className="flex-1 min-w-0">
                    <CashierPicker
                        name={value}
                        phone={cashierPhone || ''}
                        onChange={onCashierChange}
                        disabled={disabled}
                        className={`w-full text-[12px] font-semibold text-slate-800 bg-white border rounded-md px-2 py-1 outline-none ${errorText ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-[#1B4332]'}`}
                    />
                </div>
            ) : isBank ? (
                <div className="flex-1 min-w-0">
                    <BankNameInput
                        value={value}
                        onChange={onChange}
                        placeholder="Search bank name"
 className={`w-full text-[12px] font-semibold text-slate-800 bg-white border rounded-md px-2 py-1 outline-none ${errorText ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-[#1B4332]'}`}
                    />
                </div>
            ) : options ? (
                <div className="flex-1 min-w-0">
                    <Select
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        options={[
                            { value: '', label: '—' },
                            ...options.map(o => typeof o === 'string'
                                ? { value: o, label: o }
                                : { value: o.value, label: o.label }),
                        ]}
                        className="w-full text-[12px] font-semibold text-slate-800 bg-white border border-slate-200 rounded-md px-2 py-1 focus:border-[#1B4332] disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                    />
                </div>
            ) : type === 'date' ? (
                // DD-MM-YYYY on screen (the dashboard's one date format); YYYY-MM-DD in and out.
                <DatePicker value={value} onChange={onChange} disabled={disabled} placeholder="DD-MM-YYYY" align="left"
                    className="flex-1 min-w-0" buttonClassName={`w-full h-7 px-2 rounded-md shadow-none text-[12px] ${errorText ? 'border-red-300' : 'border-slate-200'}`} />
            ) : (
                <input type={type} value={value} onChange={e => onChange(e.target.value)} onFocus={onFocus} placeholder={label.includes('Date') ? 'DD-MM-YYYY' : 'Enter value'} disabled={disabled}
                    onWheel={type === 'number' ? (e => (e.target as HTMLInputElement).blur()) : undefined}
 className={`flex-1 min-w-0 text-[12px] font-semibold text-slate-800 bg-white border rounded-md px-2 py-1 outline-none ${errorText ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-[#1B4332]'} disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed`} />
            )}
            {onApply && (
                <button type="button" onClick={onApply} disabled={!value || disabled || !!errorText} title={errorText ? errorText : "Verify and fill this field in the form"}
                    className={`shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${justApplied ? 'bg-green-600 text-white' : 'bg-[#1B4332] text-white hover:bg-[#163829]'}`}>
                    <Check size={12} />
                    {justApplied ? 'Verified' : 'Verify'}
                </button>
            )}
        </div>
        {errorText && <p className="text-[10px] text-red-500 mt-1 ml-[9.5rem]">{errorText}</p>}
        {!errorText && helperText && <p className="text-[10px] text-slate-400 mt-1 ml-[9.5rem]">{helperText}</p>}
    </div>
);

export const ImagePreviewModal: React.FC<{
    url: string | null; onClose?: () => void; text?: string | null; loading?: boolean;
    onAutoFill?: (values: PaymentAutoFillValues) => void;
    // Fires when a single field is explicitly Verified — lets the payment screen lock just
    // that field. Fields never clicked Verify are never reported here and stay editable.
    onFieldVerified?: (key: keyof PaymentAutoFillValues) => void;
    // The payment type currently selected on the payment screen — the panel only ever
    // shows fields for it; the type itself is picked in the form, not here.
    activePaymentType?: string | null;
    // Remaining balance due on the order — seeds Amount when OCR found none, and caps
    // what can be entered/verified, mirroring the payment form's own Amount field.
    remaining?: number;
    // Renders as a plain panel embedded directly in the page — no backdrop, no header,
    // no close button — and stays mounted (with an empty-state placeholder) even before
    // any image has been uploaded, instead of disappearing when url is null.
    inline?: boolean;
    // Inline-only: renders an Upload/Re-upload control above the image so the whole
    // proof-of-payment interaction (upload + OCR + fields) lives in this one panel.
    uploadLabel?: string;
    uploading?: boolean;
    uploadProgress?: number;
    uploadErrorMsg?: string;
    onUpload?: (file: File) => void;
    // Lets the admin paste an already-hosted image URL instead of picking a file for the
    // main upload slot — skips the upload step and seeds the panel with that URL directly.
    onUrlSubmit?: (url: string) => void;
    // Fires when the X (clear) button is pressed, or when switching between the Image
    // Upload / Paste URL tabs — removes whatever was loaded under the previous source.
    onClear?: () => void;
    // Opens a larger view of the loaded image (e.g. a popup) — offered regardless of
    // whether it got there via file upload or a pasted URL.
    onImageZoom?: (url: string) => void;
    // Inline-only: a second, optional image slot (e.g. cheque back) shown below the main one.
    secondaryUpload?: { label: string; url: string; uploading?: boolean; error?: string; onUpload: (file: File) => void; onUrlSubmit?: (url: string) => void; onClear?: () => void; onZoom?: (url: string) => void };
    // Per-field validation errors (from the page's own validate-on-submit pass), keyed the
    // same as PaymentAutoFillValues — shown under the matching Extracted Details field.
    fieldErrors?: Partial<Record<keyof PaymentAutoFillValues, string>>;
    // Mirrors this panel's own UTR/reference duplicate check out to the page, so it can
    // gate its submit button the same way the payment form used to.
    onUtrDuplicate?: (dup: boolean) => void;
    // Inline-only: shows an optional "Surplus" field below Extracted Details — for when the
    // customer paid more than the order's due amount, and the extra is meant to go to their
    // Animalkart wallet. Controlled by the parent page so it can include it as its own
    // SURPLUS transaction on submit.
    showSurplus?: boolean;
    surplusAmount?: string;
    onSurplusChange?: (v: string) => void;
    surplusComment?: string;
    onSurplusCommentChange?: (v: string) => void;
    comment?: string;
    onCommentChange?: (v: string) => void;
    // Inline-only: render just one half of the usual side-by-side layout — 'fields' for the
    // Extracted Details/Surplus/Full Extracted Text side, 'image' for the upload side — so the
    // caller can place the two halves in its own grid (e.g. alongside a payment-method picker
    // in the same box as 'fields'). Omit to get the original combined two-column layout.
    part?: 'fields' | 'image' | 'text';
    // Fires on every keystroke/selection for a field — lets the page drop that field's stale
    // fieldError (from a previous failed submit) and un-mark it as Verified, so editing after
    // an error actually re-enables Verify instead of leaving it permanently disabled.
    onFieldEdited?: (key: keyof PaymentAutoFillValues) => void;
}> = ({
    url, onClose, text, loading, onAutoFill, onFieldVerified, activePaymentType, remaining, inline,
    uploadLabel, uploading, uploadProgress, uploadErrorMsg, onUpload, onUrlSubmit, onClear, onImageZoom, secondaryUpload, fieldErrors,
    onUtrDuplicate, showSurplus, surplusAmount = '', onSurplusChange, surplusComment = '', onSurplusCommentChange,
    comment = '', onCommentChange, part, onFieldEdited,
}) => {
    const [copied, setCopied] = useState(false);
    const [filledKeys, setFilledKeys] = useState<Set<keyof PaymentAutoFillValues>>(new Set());
    const [formValues, setFormValues] = useState<PaymentAutoFillValues>(emptyAutoFillValues());
    const [amountWarning, setAmountWarning] = useState<string | null>(null);
    const [utrDuplicate, setUtrDuplicate] = useState(false);
    const [utrChecking, setUtrChecking] = useState(false);

    useEffect(() => {
        const v = (formValues.utrNumber || '').trim();
        const needsRef = ['BANK_TRANSFER', 'CHEQUE', 'UPI'].includes(activePaymentType || '');
        if (!needsRef || v.length < 2) { setUtrDuplicate(false); onUtrDuplicate?.(false); setUtrChecking(false); return; }
        let cancelled = false;
        setUtrChecking(true);
        const t = setTimeout(async () => {
            try {
                const url = API_ENDPOINTS.checkTransactionReference(v);
                const res = await api.get(url);
                if (!cancelled) { const dup = !!res.data?.exists; setUtrDuplicate(dup); onUtrDuplicate?.(dup); }
            } catch {
                if (!cancelled) { setUtrDuplicate(false); onUtrDuplicate?.(false); }
            } finally {
                if (!cancelled) setUtrChecking(false);
            }
        }, 500);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.utrNumber, activePaymentType]);

    // Tracks which (image, has-OCR-text) combo was last seeded — reopening the SAME image
    // with the SAME (or still-missing) text must keep whatever was verified/typed before,
    // not wipe it back to fresh OCR values. Keying on whether text is present too (not just
    // the url) matters because url and text can arrive as two separate updates: the upload
    // finishes first (url goes non-empty while OCR is still running), then OCR resolves a
    // moment later (text arrives for that same url) — without that second key component,
    // the first (text-less) pass would mark the url "already seeded" and the real
    // extracted values would never make it into the fields once OCR actually finished.
    const lastSeedKeyRef = useRef<string | null>(null);
    const prevPaymentTypeRef = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        if (prevPaymentTypeRef.current !== undefined && prevPaymentTypeRef.current !== null && prevPaymentTypeRef.current !== activePaymentType) {
            setFormValues(prev => ({
                ...prev,
                transferMode: '',
                paidBy: '',
                utrNumber: '',
                accountHolderName: '',
                bankName: '',
                cashierName: '',
                cashierPhone: '',
            }));
            setFilledKeys(new Set());
        }
        prevPaymentTypeRef.current = activePaymentType;
    }, [activePaymentType]);


    // Re-seed the editable fields only when a genuinely different image/OCR result is opened.
    // If OCR found no amount, leave it blank for manual entry rather than guessing the
    // remaining balance — a guessed default looks like an extracted value and is easy to
    // Verify/submit without noticing it was never actually read off the document.
    useEffect(() => {
        if (!url) return;
        const seedKey = `${url}::${text ? '1' : '0'}`;
        if (lastSeedKeyRef.current === seedKey) return;
        lastSeedKeyRef.current = seedKey;
        const seeded = text ? seedAutoFillValues(extractPaymentFields(text)) : emptyAutoFillValues();
        // An extracted amount over the remaining balance is almost always a misread (or a
        // multi-installment receipt) — don't auto-fill it, leave Amount blank for manual entry
        // rather than seeding a value that can never be Verified.
        if (remaining && Number(seeded.txAmount) > remaining) seeded.txAmount = '';
        setFormValues(seeded);
        setFilledKeys(new Set());
        setAmountWarning(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, text]);

    if (!url && !inline) return null;
    // In the payment flow (onAutoFill present) the editable panel always shows, even when OCR
    // found nothing — fields must still be entered here, manually if needed, never on the form.
    // Inline mode is a persistent panel embedded in the page, so it always shows the
    // extracted-fields layout too, even before any image has been uploaded.
    const showText = inline || loading || !!text || !!onAutoFill;
    // Editing a field after it's been filled means the confirmed value no longer matches
    // what's in the box — go back to "Fill" so the (now different) value has to be re-applied.
    const setField = (key: keyof PaymentAutoFillValues) => (v: string) => {
        setFormValues(prev => ({ ...prev, [key]: v }));
        setFilledKeys(prev => { if (!prev.has(key)) return prev; const next = new Set(prev); next.delete(key); return next; });
        onFieldEdited?.(key);
    };

    // Amount is capped at the remaining balance — reject (rather than clamp) anything over
    // it, same behavior as the payment form's own Amount field.
    const setAmountField = (v: string) => {
        const raw = v.replace(/[^0-9]/g, '');
        const numVal = raw === '' ? 0 : Number(raw);
        if (remaining && raw !== '' && numVal > remaining) {
            setAmountWarning(`Cannot exceed remaining balance (₹${remaining.toLocaleString('en-IN')})`);
            return;
        }
        setAmountWarning(null);
        setField('txAmount')(raw);
    };

    // Applies a single field's current value to the form and marks it locked there —
    // lets the user confirm one value at a time while comparing against the image/text,
    // instead of committing everything (including unchecked mistakes) at once. Fields
    // never explicitly Verified are never sent to the form and stay editable there.
    const applyField = (key: keyof PaymentAutoFillValues) => {
        if (!onAutoFill || !formValues[key]) return;
        if (key === 'txAmount' && remaining && Number(formValues.txAmount) > remaining) return;
        onAutoFill({ ...emptyAutoFillValues(), [key]: formValues[key] });
        setFilledKeys(prev => new Set(prev).add(key));
        onFieldVerified?.(key);
    };

    const fieldSpecs: FieldSpec[] = [
        { key: 'txAmount', label: 'Amount', type: 'number', required: true },
        { key: 'txDate', label: 'Transaction Date', type: 'text', required: true },
        ...fieldsForPaymentType(activePaymentType || '', formValues.paidBy, formValues.transferMode),
    ];

    const handleCopy = () => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const imageBoxSizing = inline ? 'min-h-[420px]' : 'h-[75vh]';

    // Padding is skipped when rendered as a standalone `part` — the caller's own box
    // supplies it (so it can sit flush alongside a payment-method picker in the same box).
    const partPad = part ? '' : 'p-4';

    const imageColumnContent = (
        <div className={`${partPad} bg-slate-100/50 flex flex-col gap-3 ${inline ? '' : imageBoxSizing} ${!part ? 'lg:border-r border-slate-100' : ''}`}>
            {inline && uploadLabel && (
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{uploadLabel}</span>
                    {url && onImageZoom && (
                        <button type="button" onClick={() => onImageZoom(url)}
                            className="px-2 py-1 text-[10px] font-bold text-[#1B4332] bg-[#1B4332]/10 hover:bg-[#1B4332]/20 rounded transition-colors uppercase tracking-wider shrink-0">
                            View
                        </button>
                    )}
                </div>
            )}
            <ImageSourceTabs enableUrl={inline && !!onUrlSubmit} onModeChange={() => onClear?.()}>
                {mode => mode === 'url' ? (
                    <PasteUrlBox url={url || undefined} onSubmit={onUrlSubmit!} onClear={onClear} loading={uploading} onView={onImageZoom} previewClassName="max-w-full max-h-full rounded shadow-sm object-contain" />

                ) : (
                    <label className={`flex-1 flex justify-center items-center ${imageBoxSizing} ${inline ? 'rounded-lg border border-slate-200 bg-white overflow-hidden p-2' : ''} ${inline && onUpload && !uploading ? 'cursor-pointer hover:border-[#1B4332] transition-colors' : ''}`}>
                        {inline && onUpload && !uploading && (
                            <input type="file" accept="image/*" className="hidden"
                                onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.currentTarget.value = ''; }} />
                        )}
                        {uploading ? (
                            <div className="flex flex-col items-center gap-2 text-blue-600">
                                <Loader2 size={22} className="animate-spin" />
                                <span className="text-xs font-semibold">Uploading... {uploadProgress ?? 0}%</span>
                            </div>
                        ) : url ? (
                            <SmartImage
                                src={url} alt="Preview"
                                className={`max-w-full max-h-full rounded shadow-sm object-contain ${onImageZoom ? 'cursor-zoom-in' : ''}`}
                                onClick={onImageZoom ? (e => { e.preventDefault(); e.stopPropagation(); onImageZoom(url); }) : undefined}
                            />
                        ) : (
                            <div className="flex flex-col items-center gap-2 text-slate-400">
                                <Upload size={22} />
                                <span className="text-xs font-semibold">{inline && onUpload ? 'Click to upload' : 'No image uploaded'}</span>
                            </div>
                        )}
                    </label>
                )}
            </ImageSourceTabs>
            {uploadErrorMsg && <p className="text-[10px] text-red-500">{uploadErrorMsg}</p>}

            {inline && secondaryUpload && (
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{secondaryUpload.label}</span>
                        {secondaryUpload.url && secondaryUpload.onZoom && (
                            <button type="button" onClick={() => secondaryUpload.onZoom!(secondaryUpload.url)}
                                className="px-2 py-1 text-[10px] font-bold text-[#1B4332] bg-[#1B4332]/10 hover:bg-[#1B4332]/20 rounded transition-colors uppercase tracking-wider shrink-0">
                                View
                            </button>
                        )}
                    </div>
                    <ImageSourceTabs enableUrl={!!secondaryUpload.onUrlSubmit} onModeChange={() => secondaryUpload.onClear?.()}>
                        {mode => mode === 'url' ? (
                            <PasteUrlBox url={secondaryUpload.url} onSubmit={secondaryUpload.onUrlSubmit!} onClear={secondaryUpload.onClear} loading={secondaryUpload.uploading} onView={secondaryUpload.onZoom} previewClassName="w-full h-24 object-cover rounded-lg border border-slate-200 bg-white" />
                        ) : secondaryUpload.uploading ? (
                            <div className="flex items-center gap-1.5 text-blue-600 text-[11px]">
                                <Loader2 size={11} className="animate-spin" /> Uploading...
                            </div>
                        ) : (
                            <label className="block cursor-pointer">
                                <input type="file" accept="image/*" className="hidden"
                                    onChange={e => { const f = e.target.files?.[0]; if (f) secondaryUpload.onUpload(f); e.currentTarget.value = ''; }} />
                                {secondaryUpload.url ? (
                                    <SmartImage
                                        src={secondaryUpload.url} alt="Secondary"
                                        className={`w-full h-24 object-cover rounded-lg border border-slate-200 bg-white ${secondaryUpload.onZoom ? 'cursor-zoom-in' : ''}`}
                                        onClick={secondaryUpload.onZoom ? (e => { e.preventDefault(); e.stopPropagation(); secondaryUpload.onZoom!(secondaryUpload.url); }) : undefined}
                                    />
                                ) : (
                                    <div className="w-full h-24 flex flex-col items-center justify-center gap-1 text-slate-400 rounded-lg border border-dashed border-slate-200 bg-white hover:border-[#1B4332] transition-colors">
                                        <Upload size={16} />
                                        <span className="text-[10px] font-semibold">Click to upload</span>
                                    </div>
                                )}
                            </label>
                        )}
                    </ImageSourceTabs>
                    {secondaryUpload.error && <p className="text-[10px] text-red-500">{secondaryUpload.error}</p>}
                </div>
            )}
        </div>
    );

    const fieldsColumnContent = (
        <div className={`${partPad} space-y-4 ${inline ? '' : `${imageBoxSizing} overflow-y-auto`}`}>
                {/* Extracted Details always stays mounted — only the Full Extracted
                    Text box below shows a loading state, so re-reading a new image
                    never wipes out fields the admin is already looking at/editing. */}
                <div className="space-y-2">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 divide-y divide-slate-100">
                        {fieldSpecs.map(spec => {
                            const isAmount = spec.key === 'txAmount';
                            const amountInWords = isAmount && Math.floor(Number(formValues.txAmount) || 0) > 0
                                ? numberToWords(Math.floor(Number(formValues.txAmount)))
                                : undefined;
                            const amountHelper = isAmount
                                ? [remaining != null ? `Max ₹${remaining.toLocaleString('en-IN')}` : null, amountInWords].filter(Boolean).join(' · ')
                                : undefined;

                            const isUtr = spec.key === 'utrNumber';
                            const utrError = isUtr && utrDuplicate ? 'This transaction number already exists.' : undefined;
                            const utrHelper = isUtr && utrChecking ? 'Checking…' : undefined;
                            const isDate = spec.key === 'txDate';
                            const isCashierPhone = spec.key === 'cashierPhone';

                            return (
                                <EditableField
                                    key={spec.key}
                                    label={spec.label}
                                    type={isDate ? 'date' : spec.type}
                                    options={spec.options}
                                    isBank={spec.key === 'bankName'}
                                    isCashier={spec.isCashier}
                                    cashierPhone={formValues.cashierPhone}
                                    // Both values at once, from the roster's own
                                    // record -- so the name and number on the
                                    // voucher panel cannot come from two
                                    // different cashiers. Each still needs its
                                    // own Verify to reach the form.
                                    onCashierChange={spec.isCashier
                                        ? (next => {
                                            setField('cashierName')(next.cashierName);
                                            setField('cashierPhone')(next.cashierPhone);
                                        })
                                        : undefined}
                                    required={spec.required}
                                    value={isDate ? ddmmyyyyToIso(formValues.txDate) : formValues[spec.key]}
                                    onChange={
                                        isAmount ? setAmountField
                                            : isDate ? (v => setField('txDate')(isoToDdmmyyyy(v)))
                                                : isUtr ? (v => setField('utrNumber')(
                                                    activePaymentType === 'CHEQUE' ? v.replace(/\D/g, '') : v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
                                                ))
                                                    // Same 10-digit cap the other cashier-phone inputs enforce.
                                                    : isCashierPhone ? (v => setField('cashierPhone')(v.replace(/\D/g, '').slice(0, 10)))
                                                        : setField(spec.key)
                                    }
                                    onApply={onAutoFill ? () => applyField(spec.key) : undefined}
                                    onFocus={isAmount ? () => setAmountWarning(null) : undefined}
                                    justApplied={filledKeys.has(spec.key)}
                                    helperText={isAmount ? amountHelper : isUtr ? utrHelper : undefined}
                                    errorText={isAmount ? (amountWarning || fieldErrors?.txAmount) : isUtr ? (utrError || fieldErrors?.utrNumber) : fieldErrors?.[spec.key]}
                                />
                            );
                        })}
                        {onCommentChange && (
                            <div className="py-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] text-slate-500 w-36 shrink-0 truncate">
                                        Comment <span className="text-slate-400 font-normal normal-case">(optional)</span>
                                    </span>
                                    <input
                                        type="text"
                                        value={comment}
                                        onChange={e => onCommentChange(e.target.value)}
                                        placeholder="Add an optional comment/note"
                                        className="flex-1 min-w-0 text-[12px] font-semibold text-slate-800 bg-white border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-[#1B4332]"
                                    />
                                </div>
                            </div>
                        )}
                        {showSurplus && (
                            <div className="py-1.5">
                                <span className="block text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1.5">Surplus (Optional)</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] text-slate-500 w-28 shrink-0 truncate">Amount</span>
                                    <input
                                        type="number" min={0}
                                        value={surplusAmount}
                                        onWheel={e => (e.target as HTMLInputElement).blur()}
                                        onKeyDown={e => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                                        onChange={e => onSurplusChange?.(e.target.value.replace(/[^0-9]/g, ''))}
                                        placeholder="Enter Amount"
                                        className="flex-1 min-w-0 text-[12px] font-semibold text-slate-800 bg-white border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-amber-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                </div>
                                {Number(surplusAmount) > 0 && (
                                    <div className="ml-[9.5rem] mt-1 space-y-0.5">
                                        <p className="text-[10px] font-bold text-slate-900">{numberToWords(Number(surplusAmount))}</p>
                                        <p className="text-[11px] font-semibold text-amber-700">
                                            🪙 ₹{Number(surplusAmount).toLocaleString('en-IN')} will be added to the customer's wallet.
                                        </p>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 mt-1.5">
                                    <span className="text-[11px] text-slate-500 w-28 shrink-0 truncate">Comment</span>
                                    <input
                                        type="text"
                                        value={surplusComment}
                                        onChange={e => onSurplusCommentChange?.(e.target.value)}
                                        placeholder={`Customer over-paid by ${Number(surplusAmount) || 0}`}
                                        className="flex-1 min-w-0 text-[12px] font-semibold text-slate-800 bg-white border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-amber-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-2 hidden">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Full Extracted Text</span>
                        {!loading && text && (
                            <button type="button" onClick={handleCopy}
                                className="flex items-center gap-1 text-[10px] font-bold text-[#1B4332] hover:text-[#163829] transition-colors">
                                {copied ? <Check size={11} /> : <Copy size={11} />}
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                        )}
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        {loading ? (
                            <div className="flex items-center gap-1.5 text-slate-400 text-[11px]">
                                <Loader2 size={11} className="animate-spin" /> Reading image...
                            </div>
                        ) : (
                            <pre className="text-[11px] text-slate-700 whitespace-pre-wrap font-mono">{text}</pre>
                        )}
                    </div>
                </div>
            </div>
    );

    const textColumnContent = (
        <div className={`${partPad} space-y-2`}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Full Extracted Text</span>
                {!loading && text && (
                    <button type="button" onClick={handleCopy}
                        className="flex items-center gap-1 text-[10px] font-bold text-[#1B4332] hover:text-[#163829] transition-colors">
                        {copied ? <Check size={11} /> : <Copy size={11} />}
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                {loading ? (
                    <div className="flex items-center gap-1.5 text-slate-400 text-[11px]">
                        <Loader2 size={11} className="animate-spin" /> Reading image...
                    </div>
                ) : (
                    <pre className="text-[11px] text-slate-700 whitespace-pre-wrap font-mono">{text}</pre>
                )}
            </div>
        </div>
    );

    const body = (
        <>
            {showText ? (
                part === 'image' ? imageColumnContent
                    : part === 'fields' ? fieldsColumnContent
                        : part === 'text' ? textColumnContent
                            : (
                                <div className="flex flex-col">
                                    <div className="grid grid-cols-1 lg:grid-cols-2">
                                        {imageColumnContent}
                                        {fieldsColumnContent}
                                    </div>
                                    <div className="border-t border-slate-100 p-4">
                                        {textColumnContent}
                                    </div>
                                </div>
                            )
            ) : (
                <div className={`p-4 bg-slate-100/50 flex justify-center items-center ${imageBoxSizing}`}>
                    <SmartImage src={url as string} alt="Preview" className="max-w-full max-h-full rounded shadow-sm object-contain" />
                </div>
            )}
        </>
    );

    if (inline) {
        // A standalone `part` sits inside the caller's own box — no extra border/bg here.
        if (part) return <>{body}</>;
        return (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                {body}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={onClose}>
            <div className={`relative w-full ${showText ? 'max-w-5xl' : 'max-w-4xl'} bg-white rounded-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
                    <h3 className="text-sm font-bold text-slate-800">Image Preview</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full text-slate-500 transition-colors"><X size={20} /></button>
                </div>
                {body}
            </div>
        </div>
    );
};

// ── Payment Form ───────────────────────────────────────────────────────────────
interface PaymentFormProps {
    // Top-level mode
    paymentMethod: PaymentMethodType | null;
    setPaymentMethod: (m: PaymentMethodType) => void;
    showMultiple: boolean; // only available in add_payment view
    forceMultiple?: boolean; // hide top-level chips, lock to MULTIPLE
    // Amounts
    orderTotal: number;   // read-only for single modes
    remaining: number;    // for MULTIPLE mode display
    txAmount: string | number; setTxAmount: (v: string | number) => void; // editable in MULTIPLE mode
    onView?: (url: string, text?: string | null, loading?: boolean) => void;
    onReupload?: () => void;
    // Editing an existing transaction — open the proof + extracted-details preview the
    // moment the form mounts, instead of waiting for the admin to click View.
    autoViewExisting?: boolean;
    // Common
    txDate: string; setTxDate: (v: string) => void;
    // Bank Transfer
    transferMode: TransferMode; setTransferMode: (m: TransferMode) => void;
    utrNumber: string; setUtrNumber: (v: string) => void;
    onUtrDuplicate?: (dup: boolean) => void; // fired when the UTR/ref already exists on a paid/approved tx
    excludeTransactionId?: string; // editing an existing tx — don't flag its own current reference as a duplicate
    accountHolderName: string; setAccountHolderName: (v: string) => void;
    bankName: string; setBankName: (v: string) => void;
    screenshotUpload: UploadState; onScreenshotChange: (file: File) => void; onScreenshotUrlSubmit?: (url: string) => void; onScreenshotClear?: () => void;
    // Cash
    cashierName: string; setCashierName: (v: string) => void;
    cashierPhone: string; setCashierPhone: (v: string) => void;
    voucherUpload: UploadState; onVoucherChange: (file: File) => void; onVoucherUrlSubmit?: (url: string) => void; onVoucherClear?: () => void;
    // Cheque
    chequeFrontUpload: UploadState; onChequeFrontChange: (file: File) => void; onChequeFrontUrlSubmit?: (url: string) => void; onChequeFrontClear?: () => void;
    chequeBackUpload: UploadState; onChequeBackChange: (file: File) => void; onChequeBackUrlSubmit?: (url: string) => void; onChequeBackClear?: () => void;
    // Multiple sub-mode
    multipleSubMode: MultipleSubMode; setMultipleSubMode: (m: MultipleSubMode) => void;
    // UPI (only in multiple mode)
    upiScreenshotUpload: UploadState; onUpiScreenshotChange: (file: File) => void; onUpiScreenshotUrlSubmit?: (url: string) => void; onUpiScreenshotClear?: () => void;
    // Paid By
    paidBy: string; setPaidBy: (v: string) => void;
    // Coupon
    couponCode: string; setCouponCode: (v: string) => void;
    showCoupon?: boolean; // default true — pass false to hide coupon option
    mobile?: string; // customer mobile — used to fetch their assigned coupons
    // Coins (optional — only UserCreateOrderPage passes these)
    showCoins?: boolean;
    availableCoins?: number | null;
    coinsToRedeem?: string;
    setCoinsToRedeem?: (v: string) => void;
    effectiveCoinsRedeemed?: number;
    coinsFullyCover?: boolean;
    showWallet?: boolean;
    availableWallet?: number | null;
    walletAmountToUse?: string;
    setWalletAmountToUse?: (v: string) => void;
    walletFullyCover?: boolean;
    totalCost?: number;
    // Fields explicitly Verified via the image preview — those specific fields become
    // read-only here; anything never Verified stays normally editable.
    verifiedFields?: Set<keyof PaymentAutoFillValues>;
    // Optional comment
    comment?: string;
    setComment?: (v: string) => void;
    // Errors
    errors: Record<string, string>;
}

const paymentMethodOptions: { key: PaymentMethodType; label: string; icon: React.ReactNode }[] = [
    { key: 'BANK_TRANSFER', label: 'Bank Transfer', icon: <Building2 size={15} /> },
    { key: 'CHEQUE', label: 'Cheque', icon: <FileText size={15} /> },
    { key: 'CASH', label: 'Cash', icon: <Banknote size={15} /> },
    { key: 'UPI', label: 'UPI', icon: <Smartphone size={15} /> },
    { key: 'COUPON', label: 'Coupon', icon: <Ticket size={15} /> },
];

const multipleSubOptions: { key: MultipleSubMode; label: string; icon: React.ReactNode }[] = [
    { key: 'BANK_TRANSFER', label: 'Bank Transfer', icon: <Building2 size={13} /> },
    { key: 'CHEQUE', label: 'Cheque', icon: <FileText size={13} /> },
    { key: 'CASH', label: 'Cash', icon: <Banknote size={13} /> },
    { key: 'UPI', label: 'UPI', icon: <Smartphone size={13} /> },
    { key: 'COUPON', label: 'Coupon', icon: <Ticket size={13} /> },
];

const numberToWords = (num: number): string => {
    if (num === 0) return 'Zero Rupees Only';
    if (num === 1) return 'One Rupee Only';

    const a = [
        '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
        'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
    ];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const formatLessThanThousand = (n: number): string => {
        let str = '';
        if (n >= 100) {
            str += a[Math.floor(n / 100)] + ' Hundred ';
            n %= 100;
        }
        if (n >= 20) {
            str += b[Math.floor(n / 10)] + ' ';
            n %= 10;
        }
        if (n > 0) {
            str += a[n] + ' ';
        }
        return str.trim();
    };

    let word = '';

    if (num >= 10000000) {
        word += formatLessThanThousand(Math.floor(num / 10000000)) + ' Crore ';
        num %= 10000000;
    }

    if (num >= 100000) {
        word += formatLessThanThousand(Math.floor(num / 100000)) + ' Lakh ';
        num %= 100000;
    }

    if (num >= 1000) {
        word += formatLessThanThousand(Math.floor(num / 1000)) + ' Thousand ';
        num %= 1000;
    }

    if (num > 0) {
        word += formatLessThanThousand(num);
    }

    return word.trim() + ' Rupees Only';
};

export const PaymentForm: React.FC<PaymentFormProps> = (p) => {
    const [localWarning, setLocalWarning] = useState<string | null>(null);
    const [coinsWarning, setCoinsWarning] = useState<string | null>(null);
    const isMultiple = p.paymentMethod === 'MULTIPLE';

    const activeMode = isMultiple ? p.multipleSubMode : (p.paymentMethod as string | null);

    // Real-time duplicate check: warn if the entered UTR / cheque no / UPI reference
    // is already used by a PAID order or APPROVED transaction.
    const [utrDuplicate, setUtrDuplicate] = useState(false);
    const [utrChecking, setUtrChecking] = useState(false);
    useEffect(() => {
        const v = (p.utrNumber || '').trim();
        const needsRef = ['BANK_TRANSFER', 'CHEQUE', 'UPI'].includes(activeMode || '');
        if (!needsRef || v.length < 2) { setUtrDuplicate(false); setUtrChecking(false); p.onUtrDuplicate?.(false); return; }
        let cancelled = false;
        setUtrChecking(true);
        const t = setTimeout(async () => {
            try {
                const url = API_ENDPOINTS.checkTransactionReference(v) + (p.excludeTransactionId ? `&excludeTransactionId=${encodeURIComponent(p.excludeTransactionId)}` : '');
                const res = await api.get(url);
                if (!cancelled) { const dup = !!res.data?.exists; setUtrDuplicate(dup); p.onUtrDuplicate?.(dup); }
            } catch {
                if (!cancelled) { setUtrDuplicate(false); p.onUtrDuplicate?.(false); }
            } finally {
                if (!cancelled) setUtrChecking(false);
            }
        }, 500);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [p.utrNumber, activeMode, p.excludeTransactionId]);

    const utrDupMsg = utrDuplicate ? (
        <p className="text-[10px] text-red-500 font-semibold">This transaction number already exists.</p>
    ) : utrChecking ? (
        <p className="text-[10px] text-slate-400">Checking…</p>
    ) : null;

    // Which sub-mode is showing bank fields
    const showBankFields = activeMode === 'BANK_TRANSFER';
    const showChequeFields = activeMode === 'CHEQUE';
    const showCashFields = activeMode === 'CASH';
    const showUpiFields = activeMode === 'UPI';
    const showCouponFields = activeMode === 'COUPON';

    // These fields are never directly typable on the payment screen — they can only ever
    // be set by opening the uploaded proof's image preview, filling/checking them there
    // against the document, and clicking Verify. Upload status doesn't change this; it's
    // unconditional for every one of these keys, always.
    const locked = (_key: keyof PaymentAutoFillValues) => true;
    const lockedClass = (key: keyof PaymentAutoFillValues) => locked(key) ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : '';

    const [userCoupons, setUserCoupons] = useState<Coupon[]>([]);
    const [loadingUserCoupons, setLoadingUserCoupons] = useState(false);

    useEffect(() => {
        if (!showCouponFields || !p.mobile) { setUserCoupons([]); return; }
        setLoadingUserCoupons(true);
        couponService.getUserCoupons(p.mobile, 'ASSIGNED')
            .then(setUserCoupons)
            .finally(() => setLoadingUserCoupons(false));
    }, [showCouponFields, p.mobile]);

    return (
        <div className="space-y-4">
            {/* Top-level chips — hidden when forceMultiple */}
            {!p.forceMultiple && (
                <div className="flex flex-wrap gap-2">
                    {[
                        ...paymentMethodOptions.filter(opt => (p.showMultiple || opt.key !== 'MULTIPLE') && (p.showCoupon !== false || opt.key !== 'COUPON')),
                        ...(p.showCoins ? [{ key: 'COINS_REDEEM' as PaymentMethodType, label: 'Coins', icon: <span className="text-base leading-none">🪙</span> }] : []),
                        ...(p.showWallet ? [{ key: 'WALLET' as PaymentMethodType, label: 'Wallet', icon: <span className="text-base leading-none">💼</span> }] : []),
                    ].map(({ key, label, icon }) => (
                        <button key={key} type="button" onClick={() => p.setPaymentMethod(key)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${p.paymentMethod === key ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                            {icon}{label}
                        </button>
                    ))}
                </div>
            )}

            {p.paymentMethod !== null && p.paymentMethod !== 'NONE' && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">

                    {/* MULTIPLE: remaining balance banner + sub-mode chips */}
                    {isMultiple && (
                        <>
                            <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                                <span className="text-xs text-blue-600 font-medium">Remaining Balance</span>
                                <span className="text-sm font-bold text-blue-800">₹{p.remaining.toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {multipleSubOptions.filter(opt => p.showCoupon !== false || opt.key !== 'COUPON').map(({ key, label, icon }) => (
                                    <button key={key} type="button" onClick={() => p.setMultipleSubMode(key)}
                                        className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold border transition-all ${p.multipleSubMode === key ? 'bg-purple-600 text-white border-purple-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-purple-400 hover:text-purple-600'}`}>
                                        {icon}{label}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}



                    {/* Coins UI */}
                    {p.paymentMethod === 'COINS_REDEEM' && p.showCoins && (
                        (p.availableCoins ?? 0) <= 0 ? (
                            <div className="rounded-lg px-3 py-2 text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-500 text-center">
                                🪙 No coins available
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="space-y-1 w-96">
                                    <div className="flex items-center justify-between">
                                        <label className="block text-xs font-medium text-slate-600">
                                            Coins <span className="text-amber-600 font-normal">(Available: {(p.availableCoins ?? 0).toLocaleString('en-IN')} coins)</span> <span className="text-red-500">*</span>
                                        </label>
                                        <button type="button"
                                            onClick={() => { p.setCoinsToRedeem?.(String(Math.min(p.availableCoins ?? 0, p.totalCost ?? 0))); setCoinsWarning(null); }}
                                            className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider">MAX</button>
                                    </div>
                                    <input
                                        type="number" min={1}
                                        value={p.coinsToRedeem ?? ''}
                                        onWheel={e => e.currentTarget.blur()}
                                        onKeyDown={e => { if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                                        onChange={e => {
                                            const raw = e.target.value.replace(/[^0-9]/g, '');
                                            const numVal = raw === '' ? 0 : Number(raw);
                                            const avail = p.availableCoins ?? 0;
                                            if (raw !== '' && numVal > avail) {
                                                setCoinsWarning(`Cannot exceed available coins (${avail.toLocaleString('en-IN')})`);
                                            } else {
                                                p.setCoinsToRedeem?.(raw);
                                                setCoinsWarning(null);
                                            }
                                        }}
                                        placeholder={`Enter coins (Available: ${(p.availableCoins ?? 0).toLocaleString('en-IN')})`}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${(p.errors.coinsToRedeem || coinsWarning) ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-amber-500'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                    />
                                    {coinsWarning && <p className="text-[10px] text-red-500">{coinsWarning}</p>}
                                    {!coinsWarning && p.errors.coinsToRedeem && <p className="text-[10px] text-red-500">{p.errors.coinsToRedeem}</p>}
                                    {(() => {
                                        const amt = Math.floor(Number(p.coinsToRedeem) || 0);
                                        return amt > 0 ? <p className="text-[10px] font-bold text-slate-900 mt-1.5">{numberToWords(amt).replace('Rupees Only', 'Coins Only')}</p> : null;
                                    })()}
                                </div>
                                {p.coinsFullyCover && (
                                    <div className="rounded-lg px-3 py-2 text-xs font-bold bg-green-50 border border-green-200 text-green-700 text-center">
                                        ✓ Order fully covered by coins!
                                    </div>
                                )}
                            </div>
                        )
                    )}

                    {/* Wallet UI */}
                    {p.paymentMethod === 'WALLET' && p.showWallet && (
                        (p.availableWallet ?? 0) <= 0 ? (
                            <div className="rounded-lg px-3 py-2 text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-500 text-center">
                                💼 No wallet balance available
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="space-y-1 w-96">
                                    <div className="flex items-center justify-between">
                                        <label className="block text-xs font-medium text-slate-600">
                                            Wallet Amount <span className="text-amber-600 font-normal">(Available: ₹{(p.availableWallet ?? 0).toLocaleString('en-IN')})</span> <span className="text-red-500">*</span>
                                        </label>
                                        <button type="button"
                                            onClick={() => { p.setWalletAmountToUse?.(String(Math.min(p.availableWallet ?? 0, p.totalCost ?? 0))); }}
                                            className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider">MAX</button>
                                    </div>
                                    <input
                                        type="number" min={1}
                                        value={p.walletAmountToUse ?? ''}
                                        onWheel={e => e.currentTarget.blur()}
                                        onKeyDown={e => { if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                                        onChange={e => {
                                            const raw = e.target.value.replace(/[^0-9]/g, '');
                                            const numVal = raw === '' ? 0 : Number(raw);
                                            const avail = p.availableWallet ?? 0;
                                            if (raw !== '' && numVal > avail) return;
                                            p.setWalletAmountToUse?.(raw);
                                        }}
                                        placeholder={`Enter wallet amount (Available: ₹${(p.availableWallet ?? 0).toLocaleString('en-IN')})`}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${p.errors.walletAmountToUse ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-[#1B4332]'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                    />
                                    {p.errors.walletAmountToUse && <p className="text-[10px] text-red-500">{p.errors.walletAmountToUse}</p>}
                                    {(() => {
                                        const amt = Math.floor(Number(p.walletAmountToUse) || 0);
                                        return amt > 0 ? <p className="text-[10px] font-bold text-slate-900 mt-1.5">{numberToWords(amt)} Rupees Only</p> : null;
                                    })()}
                                </div>
                            </div>
                        )
                    )}

                    {/* Amount + Transaction Date */}
                    {!showCouponFields && p.paymentMethod !== 'COINS_REDEEM' && p.paymentMethod !== 'WALLET' && (
                        <div className="flex flex-wrap items-start gap-4">
                            <div className="space-y-1 w-96">
                                <div className="flex items-center justify-between">
                                    <label className="block text-xs font-medium text-slate-600">
                                        Amount (₹) <span className="text-blue-500 font-normal">(Remaining: ₹{p.remaining.toLocaleString('en-IN')})</span> <span className="text-red-500">*</span>
                                    </label>
                                    {!locked('txAmount') && (
                                        <button type="button" onClick={() => { p.setTxAmount(p.remaining); setLocalWarning(null); }}
                                            className="text-[10px] font-bold text-[#1B4332] hover:text-[#163829] uppercase tracking-wider">MAX</button>
                                    )}
                                </div>
                                <input type="number" min={1} max={p.remaining}
                                    value={p.txAmount}
                                    readOnly={locked('txAmount')}
                                    onWheel={e => e.currentTarget.blur()}
                                    onKeyDown={e => {
                                        if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onChange={e => {
                                        if (locked('txAmount')) return;
                                        const raw = e.target.value.replace(/[^0-9]/g, '');
                                        const numVal = raw === '' ? 0 : Number(raw);
                                        if (raw !== '' && numVal > p.remaining) {
                                            setLocalWarning('Entering amount greater than remaining');
                                        } else {
                                            p.setTxAmount(raw);
                                            setLocalWarning(null);
                                        }
                                    }}
                                    placeholder="Enter amount"
                                    title={locked('txAmount') ? 'Edit this from the uploaded payment doc preview' : undefined}
                                    className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${(p.errors.txAmount || localWarning) ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('txAmount')} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`} />
                                {localWarning && <p className="text-[10px] text-red-500">{localWarning}</p>}
                                {!localWarning && p.errors.txAmount && <p className="text-[10px] text-red-500">{p.errors.txAmount}</p>}
                                {(() => {
                                    const amt = Math.floor(Number(p.txAmount) || 0);
                                    if (amt > 0) {
                                        return (
                                            <p className="text-[10px] font-bold text-slate-900 mt-1.5">
                                                {numberToWords(amt)}
                                            </p>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                            <div className="space-y-1 w-64">
                                <label className="block text-xs font-medium text-slate-600">Transaction Date <span className="text-red-500">*</span></label>
                                <input type="text" placeholder="DD-MM-YYYY" value={p.txDate} onChange={e => !locked('txDate') && p.setTxDate(e.target.value)}
                                    readOnly={locked('txDate')}
                                    title={locked('txDate') ? 'Edit this from the uploaded payment doc preview' : undefined}
                                    className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${p.errors.txDate ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('txDate')} ${locked('txDate') ?'pointer-events-none':''}`} />
                                {p.errors.txDate && <p className="text-[10px] text-red-500">{p.errors.txDate}</p>}
                            </div>
                        </div>
                    )}

                    {/* ── Bank Transfer (single or MULTIPLE sub-mode) ── */}
                    {showBankFields && (
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <label className="block text-xs font-semibold text-slate-600">Transfer Mode <span className="text-red-500">*</span></label>
                                <div className="flex gap-2 flex-wrap">
                                    {TRANSFER_MODES.map(m => (
                                        <button key={m} type="button" disabled={locked('transferMode')} onClick={() => p.setTransferMode(m)}
                                            title={locked('transferMode') ? 'Edit this from the uploaded payment doc preview' : undefined}
                                            className={`px-3 py-1 rounded-lg text-xs font-bold border transition-all disabled:cursor-not-allowed disabled:opacity-60 ${p.transferMode === m ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                                            {transferModeLabel(m)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-wrap items-start gap-4">
                                <div className="space-y-1 w-40">
                                    <label className="block text-xs font-medium text-slate-600">Paid By {p.transferMode !== 'CASH_DEPOSIT' && <span className="text-red-500">*</span>}</label>
                                    <Select
                                        value={p.paidBy}
                                        onChange={(v) => p.setPaidBy(v)}
                                        disabled={locked('paidBy')}
                                        options={[
                                            { value: 'Self', label: 'Self' },
                                            { value: 'Family Member', label: 'Family Member' },
                                        ]}
                                        className={`w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#1B4332] disabled:cursor-not-allowed ${lockedClass('paidBy')}`}
                                    />
                                </div>
                                <div className="space-y-1 w-64">
                                    <label className="block text-xs font-medium text-slate-600">UTR Number {p.transferMode !== 'CASH_DEPOSIT' && <span className="text-red-500">*</span>}</label>
                                    <input type="text" value={p.utrNumber} onChange={e => p.setUtrNumber(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
                                        placeholder="UTR / Reference number"
                                        readOnly={locked('utrNumber')}
                                        title={locked('utrNumber') ? 'Edit this from the uploaded payment doc preview' : undefined}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${(p.errors.utrNumber || utrDuplicate) ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('utrNumber')}`} />
                                    {p.errors.utrNumber && <p className="text-[10px] text-red-500">{p.errors.utrNumber}</p>}
                                    {utrDupMsg}
                                </div>
                                {p.paidBy !== 'Self' && (
                                    <div className="space-y-1 w-64">
                                        <label className="block text-xs font-medium text-slate-600">Bank Holder Name {p.transferMode !== 'CASH_DEPOSIT' && <span className="text-red-500">*</span>}</label>
                                        <input type="text" value={p.accountHolderName} onChange={e => p.setAccountHolderName(e.target.value.replace(/[^a-zA-Z ]/g, ''))}
                                            placeholder="Bank holder name"
                                            readOnly={locked('accountHolderName')}
                                            title={locked('accountHolderName') ? 'Edit this from the uploaded payment doc preview' : undefined}
                                            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${p.errors.accountHolderName ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('accountHolderName')}`} />
                                        {p.errors.accountHolderName && <p className="text-[10px] text-red-500">{p.errors.accountHolderName}</p>}
                                    </div>
                                )}
                                <div className="space-y-1 w-64">
                                    <label className="block text-xs font-medium text-slate-600">Bank Name {p.transferMode !== 'CASH_DEPOSIT' && <span className="text-red-500">*</span>}</label>
                                    <BankNameInput
                                        value={p.bankName}
                                        onChange={p.setBankName}
                                        placeholder="Search bank name"
                                        disabled={locked('bankName')}
                                        className={`w-full px-3 py-2 pr-8 rounded-lg border text-sm outline-none ${p.errors.bankName ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('bankName')}`}
                                    />
                                    {p.errors.bankName && <p className="text-[10px] text-red-500">{p.errors.bankName}</p>}
                                </div>
                            </div>
                            <div className="w-full">
                                <FileUpload label="Upload Payment doc" required state={p.screenshotUpload}
                                    onChange={p.onScreenshotChange} onUrlSubmit={p.onScreenshotUrlSubmit} onClear={p.onScreenshotClear} onView={p.onView} onReupload={p.onReupload} errorMsg={p.errors.screenshotUpload} autoView={p.autoViewExisting} />
                            </div>
                        </div>
                    )}

                    {/* ── Cash ── */}
                    {showCashFields && (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-start gap-4">
                                <div className="space-y-1 w-64">
                                    <label className="block text-xs font-medium text-slate-600">Cashier Name <span className="text-red-500">*</span></label>
                                    <input type="text" value={p.cashierName} onChange={e => p.setCashierName(e.target.value)}
                                        placeholder="Cashier name"
                                        readOnly={locked('cashierName')}
                                        title={locked('cashierName') ? 'Edit this from the uploaded voucher preview' : undefined}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${p.errors.cashierName ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('cashierName')}`} />
                                    {p.errors.cashierName && <p className="text-[10px] text-red-500">{p.errors.cashierName}</p>}
                                </div>
                                <div className="space-y-1 w-52">
                                    <label className="block text-xs font-medium text-slate-600">Cashier Phone <span className="text-red-500">*</span></label>
                                    <input type="tel" value={p.cashierPhone}
                                        onChange={e => {
                                            if (locked('cashierPhone')) return;
                                            const val = e.target.value.replace(/\D/g, '');
                                            if (val.length <= 10) p.setCashierPhone(val);
                                        }}
                                        placeholder="10-digit mobile number"
                                        maxLength={10}
                                        readOnly={locked('cashierPhone')}
                                        title={locked('cashierPhone') ? 'Edit this from the uploaded voucher preview' : undefined}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${p.errors.cashierPhone ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('cashierPhone')}`} />
                                    {p.errors.cashierPhone && <p className="text-[10px] text-red-500">{p.errors.cashierPhone}</p>}
                                </div>
                            </div>
                            <div className="w-full">
                                <FileUpload label="Upload Voucher image" required state={p.voucherUpload}
                                    onChange={p.onVoucherChange} onUrlSubmit={p.onVoucherUrlSubmit} onClear={p.onVoucherClear} onView={p.onView} onReupload={p.onReupload} errorMsg={p.errors.voucherUpload} autoView={p.autoViewExisting} />
                            </div>
                        </div>
                    )}

                    {/* ── Cheque ── */}
                    {showChequeFields && (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-start gap-4">
                                <div className="space-y-1 w-64">
                                    <label className="block text-xs font-medium text-slate-600">Cheque Number <span className="text-red-500">*</span></label>
                                    <input type="text" value={p.utrNumber} onChange={e => p.setUtrNumber(e.target.value.replace(/\D/g, ''))}
                                        placeholder="Cheque number" maxLength={30}
                                        readOnly={locked('utrNumber')}
                                        title={locked('utrNumber') ? 'Edit this from the uploaded cheque image preview' : undefined}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${(p.errors.utrNumber || utrDuplicate) ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('utrNumber')}`} />
                                    {p.errors.utrNumber && <p className="text-[10px] text-red-500">{p.errors.utrNumber}</p>}
                                    {utrDupMsg}
                                </div>
                            </div>
                            <div className="flex flex-col gap-4">
                                <div className="w-full">
                                    <FileUpload label="Cheque Front Image" required state={p.chequeFrontUpload}
                                        onChange={p.onChequeFrontChange} onUrlSubmit={p.onChequeFrontUrlSubmit} onClear={p.onChequeFrontClear} onView={p.onView} onReupload={p.onReupload} errorMsg={p.errors.chequeFrontUpload} autoView={p.autoViewExisting} />
                                </div>
                                <div className="w-full">
                                    <FileUpload label="Cheque Back Image (Optional)" state={p.chequeBackUpload}
                                        onChange={p.onChequeBackChange} onUrlSubmit={p.onChequeBackUrlSubmit} onClear={p.onChequeBackClear} onView={p.onView} onReupload={p.onReupload} errorMsg={p.errors.chequeBackUpload} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── UPI (only in MULTIPLE sub-mode) ── */}
                    {showUpiFields && (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-start gap-4">
                                <div className="space-y-1 w-40">
                                    <label className="block text-xs font-medium text-slate-600">Paid By <span className="text-red-500">*</span></label>
                                    <Select
                                        value={p.paidBy}
                                        onChange={(v) => p.setPaidBy(v)}
                                        disabled={locked('paidBy')}
                                        options={[
                                            { value: 'Self', label: 'Self' },
                                            { value: 'Family Member', label: 'Family Member' },
                                        ]}
                                        className={`w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#1B4332] disabled:cursor-not-allowed ${lockedClass('paidBy')}`}
                                    />
                                </div>
                                <div className="space-y-1 w-64">
                                    <label className="block text-xs font-medium text-slate-600">UTR / Reference No. <span className="text-red-500">*</span></label>
                                    <input type="text" value={p.utrNumber} onChange={e => p.setUtrNumber(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
                                        placeholder="12-22 alphanumeric" maxLength={22}
                                        readOnly={locked('utrNumber')}
                                        title={locked('utrNumber') ? 'Edit this from the uploaded payment doc preview' : undefined}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${(p.errors.utrNumber || utrDuplicate) ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('utrNumber')}`} />
                                    {p.errors.utrNumber && <p className="text-[10px] text-red-500">{p.errors.utrNumber}</p>}
                                    {utrDupMsg}
                                </div>
                                {p.paidBy !== 'Self' && (
                                    <div className="space-y-1 w-64">
                                        <label className="block text-xs font-medium text-slate-600">UPI Holder Name (Optional)</label>
                                        <input type="text" value={p.accountHolderName} onChange={e => p.setAccountHolderName(e.target.value.replace(/[^a-zA-Z ]/g, ''))}
                                            placeholder="UPI holder name"
                                            readOnly={locked('accountHolderName')}
                                            title={locked('accountHolderName') ? 'Edit this from the uploaded payment doc preview' : undefined}
                                            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${p.errors.accountHolderName ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'} ${lockedClass('accountHolderName')}`} />
                                        {p.errors.accountHolderName && <p className="text-[10px] text-red-500">{p.errors.accountHolderName}</p>}
                                    </div>
                                )}
                            </div>
                            <div className="w-full">
                                <FileUpload label="Payment screenshot" required state={p.upiScreenshotUpload}
                                    onChange={p.onUpiScreenshotChange} onUrlSubmit={p.onUpiScreenshotUrlSubmit} onClear={p.onUpiScreenshotClear} onView={p.onView} onReupload={p.onReupload} errorMsg={p.errors.upiScreenshotUpload} autoView={p.autoViewExisting} />
                            </div>
                        </div>
                    )}

                    {/* ── Coupon ── */}
                    {showCouponFields && (
                        <>
                            <div className="space-y-1 w-72">
                                <label className="block text-xs font-medium text-slate-600">Coupon Code <span className="text-red-500">*</span></label>
                                <Select
                                    value={p.couponCode}
                                    onChange={(v) => p.setCouponCode(v)}
                                    disabled={loadingUserCoupons || userCoupons.length === 0}
                                    options={[
                                        {
                                            value: '',
                                            label: loadingUserCoupons ? 'Loading coupons…' : userCoupons.length === 0 ? 'No coupons available' : 'Select a coupon',
                                        },
                                        ...userCoupons.map(c => ({
                                            value: c.code,
                                            label: `${c.code} — ₹${Number(c.amount).toLocaleString('en-IN')}`,
                                        })),
                                    ]}
                                    className={`w-full px-3 py-2 rounded-lg border text-sm disabled:opacity-50 disabled:bg-slate-50 ${p.errors.couponCode ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'}`}
                                />
                                {!loadingUserCoupons && userCoupons.length === 0 && (
                                    <p className="text-[10px] text-slate-500">This customer has no coupons assigned.</p>
                                )}
                                {p.errors.couponCode && <p className="text-[10px] text-red-500">{p.errors.couponCode}</p>}
                            </div>
                            {p.couponCode && (() => {
                                const selectedCoupon = userCoupons.find(c => c.code === p.couponCode);
                                if (!selectedCoupon) return null;
                                const couponAmt = Number(selectedCoupon.amount) || 0;
                                const newRemaining = Math.max(p.remaining - couponAmt, 0);
                                return (
                                    <div className="w-full max-w-sm bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 space-y-1.5">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-slate-600">Remaining Balance</span>
                                            <span className="font-semibold text-slate-800">₹{p.remaining.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-slate-600">Coupon Amount</span>
                                            <span className="font-semibold text-red-600">- ₹{couponAmt.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm pt-1.5 border-t border-blue-200">
                                            <span className="font-medium text-blue-700">Remaining After Coupon</span>
                                            <span className="font-bold text-blue-800">₹{newRemaining.toLocaleString('en-IN')}</span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    )}

                    {/* ── Comment (optional) ── */}
                    {p.setComment && (
                        <div className="space-y-1 w-full pt-1">
                            <label className="block text-xs font-medium text-slate-600">
                                Comment <span className="text-slate-400 font-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                value={p.comment || ''}
                                onChange={e => p.setComment?.(e.target.value)}
                                placeholder="Add an optional comment/note"
                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#1B4332]"
                            />
                        </div>
                    )}

                </div>
            )}

        </div>
    );
};

// ── Existing transactions list (for add_payment + MULTIPLE mode) ───────────────
const ExistingTransactionsList: React.FC<{
    transactions: any[];
    totalCost: number;
    totalPaid: number;
    remaining: number;
    loading: boolean;
    onViewImage?: (url: string) => void;
}> = ({ transactions, totalCost, totalPaid, remaining, loading, onViewImage }) => {
    if (loading) {
        return (
            <div className="flex items-center gap-2 py-3 text-blue-500">
                <Loader2 size={14} className="animate-spin" />
                <span className="text-xs">Loading previous payments...</span>
            </div>
        );
    }
    if (transactions.length === 0) return null;

    const methodLabel = (m: string) => m.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    return (
        <div className="space-y-2">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Previous Payments</p>
            <div className="space-y-1.5">
                {transactions.map((tx: any, i: number) => {
                    const method = tx.paymentMethod || tx.paymentType || '-';
                    const imageUrls: string[] = [];
                    if (tx.paymentScreenshotUrl) imageUrls.push(tx.paymentScreenshotUrl);
                    if (tx.payment_screenshot_url) imageUrls.push(tx.payment_screenshot_url);
                    if (tx.voucherImageUrl) imageUrls.push(tx.voucherImageUrl);
                    if (tx.voucher_image_url) imageUrls.push(tx.voucher_image_url);
                    if (tx.chequeFrontImage) imageUrls.push(tx.chequeFrontImage);
                    if (tx.cheque_front_image) imageUrls.push(tx.cheque_front_image);
                    if (tx.chequeBackImage) imageUrls.push(tx.chequeBackImage);
                    if (tx.cheque_back_image) imageUrls.push(tx.cheque_back_image);

                    const uniqueImages = Array.from(new Set(imageUrls)).filter(Boolean);

                    return (
                        <div key={tx.id || i} className="flex flex-col bg-green-50 border border-green-100 rounded-lg px-3 py-2 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle size={13} className="text-green-500 shrink-0" />
                                    <div>
                                        <span className="text-xs font-semibold text-slate-700">{methodLabel(method)}</span>
                                        {tx.transferMode && <span className="text-[10px] text-slate-400 ml-1">({tx.transferMode})</span>}
                                        {tx.transactionDate && <p className="text-[10px] text-slate-400">{tx.transactionDate}</p>}
                                    </div>
                                </div>
                                <span className="text-sm font-bold text-green-700">₹{Number(tx.amount || 0).toLocaleString('en-IN')}</span>
                            </div>
                            {uniqueImages.length > 0 && (
                                <div className="flex gap-2 items-center pt-1.5 border-t border-green-100/60">
                                    <span className="text-[10px] text-slate-500 font-semibold shrink-0">Receipts:</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {uniqueImages.map((imgUrl, imgIdx) => (
                                            <div key={imgIdx} className="relative cursor-pointer" onClick={() => onViewImage?.(imgUrl)}>
                                                <SmartImage src={imgUrl} alt="Receipt Thumbnail" className="w-8 h-8 object-cover rounded border border-green-200 bg-white hover:border-green-400 transition-colors" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="grid grid-cols-3 gap-2 pt-1">
                <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-[10px] text-slate-400 font-medium">Total</p>
                    <p className="text-xs font-bold text-slate-700">₹{totalCost.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-green-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-[10px] text-green-600 font-medium">Paid</p>
                    <p className="text-xs font-bold text-green-700">₹{totalPaid.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-amber-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-[10px] text-amber-600 font-medium">Remaining</p>
                    <p className="text-xs font-bold text-amber-700">₹{remaining.toLocaleString('en-IN')}</p>
                </div>
            </div>
        </div>
    );
};

// ── Main Modal ─────────────────────────────────────────────────────────────────
export const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
    isOpen, onClose, onSuccess, user, adminMobile, adminName, adminRole, initialView = 'list', preSelectedOrder,
}) => {
    const [view, setView] = useState<ModalView>('list');
    const [previewUrl, setPreviewUrl] = useState<{ url: string; text?: string | null; loading?: boolean } | null>(null);
    const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<PendingOrder | null>(null);
    const lastTotalRef = useRef<number>(0);

    const [products, setProducts] = useState<{ id: string; name?: string; price: number }[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [farms, setFarms] = useState<{ id: string; location?: string }[]>([]);
    const [loadingFarms, setLoadingFarms] = useState(false);

    // Create order state
    const [breedId, setBreedId] = useState('');
    const [numUnits, setNumUnits] = useState<number>(1);
    const [baseUnitCost, setBaseUnitCost] = useState<number>(0);
    const [farmId, setFarmId] = useState('');
    const [createdDate, setCreatedDate] = useState(new Date().toISOString().split('T')[0]);

    // Payment state
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType | null>('BANK_TRANSFER');
    const [txDate, setTxDate] = useState('');
    const [transferMode, setTransferMode] = useState<TransferMode>('NEFT');
    const [utrNumber, setUtrNumber] = useState('');
    const [accountHolderName, setAccountHolderName] = useState('');
    const [bankName, setBankName] = useState('');
    const [cashierName, setCashierName] = useState('');
    const [cashierPhone, setCashierPhone] = useState('');

    // Multiple mode state
    const [multipleSubMode, setMultipleSubMode] = useState<MultipleSubMode>('BANK_TRANSFER');
    const [txAmount, setTxAmount] = useState<string | number>('');
    const [existingTransactions, setExistingTransactions] = useState<any[]>([]);
    const [totalPaid, setTotalPaid] = useState<number>(0);
    const [remaining, setRemaining] = useState<number>(0);
    const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

    // Image upload states
    const [screenshotUpload, setScreenshotUpload] = useState<UploadState>(emptyUpload());
    const [voucherUpload, setVoucherUpload] = useState<UploadState>(emptyUpload());
    const [chequeFrontUpload, setChequeFrontUpload] = useState<UploadState>(emptyUpload());
    const [chequeBackUpload, setChequeBackUpload] = useState<UploadState>(emptyUpload());
    const [upiScreenshotUpload, setUpiScreenshotUpload] = useState<UploadState>(emptyUpload());
    const [paidBy, setPaidBy] = useState<string>('Self');
    const [couponCode, setCouponCode] = useState<string>('');
    const [comment, setComment] = useState<string>('');

    // Coins redeem state
    const [availableCoins, setAvailableCoins] = useState<number | null>(null);
    const [loadingCoins, setLoadingCoins] = useState(false);
    const [redeemCoins, setRedeemCoins] = useState<boolean>(false);
    const [coinRedeemMode, setCoinRedeemMode] = useState<'all' | 'custom' | null>(null);
    const [coinsToRedeem, setCoinsToRedeem] = useState<string>('');

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const anyUploading = screenshotUpload.uploading || voucherUpload.uploading ||
        chequeFrontUpload.uploading || chequeBackUpload.uploading || upiScreenshotUpload.uploading;

    const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.mobile : '';
    const totalCost = baseUnitCost * numUnits;

    const effectiveCoinsRedeemed = redeemCoins
        ? Math.min(coinRedeemMode === 'all' ? (availableCoins ?? 0) : (Number(coinsToRedeem) || 0), totalCost)
        : 0;
    const remainingAfterCoins = Math.max(totalCost - effectiveCoinsRedeemed, 0);
    const coinsFullyCover = redeemCoins && totalCost > 0 && remainingAfterCoins === 0;

    const updateError = (field: string) => {
        setErrors(prev => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    useEffect(() => {
        if (isOpen && user) {
            resetPaymentState();
            setView(initialView);
            setSelectedOrder(preSelectedOrder ?? null);
            fetchPendingOrders();
            fetchProducts();
            fetchFarms();
            resetOrderState();
            fetchAvailableCoins(user.mobile);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Handle auto-sync for txAmount (defaulting to the full amount for partial entry)
    useEffect(() => {
        const currentRefValue = lastTotalRef.current;
        let newTotal = 0;

        if (view === 'create_order') {
            newTotal = remainingAfterCoins;
        } else if (view === 'add_payment' && selectedOrder && !isLoadingTransactions) {
            newTotal = remaining;
        }

        // Only auto-fill if the total cost changed (e.g. units/breed changed)
        // or if we are just initializing (currentRefValue === 0)
        if (newTotal !== currentRefValue) {
            setTxAmount(newTotal.toString());
            lastTotalRef.current = newTotal;
        }
    }, [view, remainingAfterCoins, remaining, isLoadingTransactions, selectedOrder]);

    // Fetch existing transactions when entering add_payment view
    useEffect(() => {
        if (view === 'add_payment' && selectedOrder) {
            const orderTotal = selectedOrder.totalCost || 0;
            setRemaining(orderTotal);
            setTxAmount(orderTotal);
            fetchExistingTransactions(selectedOrder.id, orderTotal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, selectedOrder?.id]);

    // Auto-toggle payment method when coins fully cover the order
    useEffect(() => {
        if (view !== 'create_order') return;
        if (coinsFullyCover) {
            setPaymentMethod('NONE');
        } else if (redeemCoins) {
            setPaymentMethod('BANK_TRANSFER');
        }
    }, [coinsFullyCover, redeemCoins, view]);

    const resetPaymentState = () => {
        setPaymentMethod('BANK_TRANSFER');
        setTxDate(''); setTransferMode('NEFT'); setUtrNumber('');
        setAccountHolderName(''); setBankName('');
        setCashierName(''); setCashierPhone('');
        setMultipleSubMode('BANK_TRANSFER');
        setExistingTransactions([]); setTotalPaid(0); setRemaining(0);
        setScreenshotUpload(emptyUpload()); setVoucherUpload(emptyUpload());
        setChequeFrontUpload(emptyUpload()); setChequeBackUpload(emptyUpload());
        setUpiScreenshotUpload(emptyUpload());
        setPaidBy('Self');
        setCouponCode('');
        setComment('');
        setErrors({});
    };

    const resetOrderState = () => {
        const defaultBreedId = products.length > 0 ? products[0].id : '';
        const defaultCost = products.length > 0 ? products[0].price : 0;
        const defaultFarmId = farms.length > 0 ? farms[0].id : '';

        setBreedId(defaultBreedId);
        setNumUnits(1);
        setBaseUnitCost(defaultCost);
        setFarmId(defaultFarmId);
        setCreatedDate(new Date().toISOString().split('T')[0]);
        setRedeemCoins(false);
        setCoinRedeemMode(null);
        setCoinsToRedeem('');
    };

    // ── Fetch available coins ──────────────────────────────────────────────────
    const fetchAvailableCoins = async (mobile: string) => {
        setLoadingCoins(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getCoinTransaction(mobile));
            if (response?.data?.statuscode === 200 && response?.data?.stats) {
                setAvailableCoins(Math.floor(response.data.stats.remaining_coins ?? 0));
            } else {
                setAvailableCoins(0);
            }
        } catch {
            setAvailableCoins(0);
        } finally {
            setLoadingCoins(false);
        }
    };

    // ── Fetch existing transactions ────────────────────────────────────────────
    const fetchExistingTransactions = async (orderId: string, orderTotal: number) => {
        setIsLoadingTransactions(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getOrderDetails(orderId));
            if (response?.data?.status === 'success') {
                const txns: any[] = response.data.transactions || [];
                const orderData = response.data.order || {};
                if (response.data.order) {
                    setSelectedOrder(response.data.order);
                }
                const currentOrderTotal = orderData.totalCost ?? orderTotal;
                const paid = txns.reduce((sum: number, t: any) => sum + (Number(t.amount) || 0), 0);
                const rem = Math.max(currentOrderTotal - paid, 0);
                setExistingTransactions(txns);
                setTotalPaid(paid);
                setRemaining(rem);
                setTxAmount(rem);
                // Auto-switch to MULTIPLE if prior transactions exist
                if (txns.length > 0) {
                    setPaymentMethod('MULTIPLE');
                    setMultipleSubMode('BANK_TRANSFER');
                }
                if (rem === 0) {
                    setSnackbar({ message: 'Payment completed! Closing payment screen...', type: 'success' });
                    setTimeout(() => {
                        onSuccess();
                        onClose();
                    }, 1000);
                }
            }
        } catch {
            setExistingTransactions([]);
        } finally {
            setIsLoadingTransactions(false);
        }
    };

    // ── Firebase file upload ───────────────────────────────────────────────────
    const handleFileUpload = async (
        file: File,
        setUpload: React.Dispatch<React.SetStateAction<UploadState>>,
        pathSuffix: string,
        errorKey?: string,
    ) => {
        if (!user) return;
        if (errorKey) updateError(errorKey);
        setUpload(prev => ({ ...prev, file, url: '', uploading: true, error: '', progress: 0 }));
        try {
            // ── Image Compression ──────────────────────────────────────────────────
            let finalFile = file;
            if (file.type.startsWith('image/')) {
                const options = {
                    maxSizeMB: 1,           // Max size in MB
                    maxWidthOrHeight: 1920, // Max width/height
                    useWebWorker: true,
                };
                try {
                    finalFile = await imageCompression(file, options);
                    console.log(`Compressed from ${file.size / 1024 / 1024}MB to ${finalFile.size / 1024 / 1024}MB`);
                } catch (e) {
                    console.error("Compression failed, using original file", e);
                }
            }

            const path = `animalkart/${storageEnv}/${user.mobile}/payments/${Date.now()}_${pathSuffix}_${file.name}`;
            const url = await uploadToFirebase(finalFile, path, pct => setUpload(prev => ({ ...prev, progress: pct })));
            setUpload(prev => ({ ...prev, file: finalFile, url, uploading: false }));
        } catch {
            setUpload(prev => ({ ...prev, uploading: false, error: 'Upload failed. Please try again.' }));
        }
    };

    // Seeds an upload tile with a pasted image URL directly — no file, no Firebase upload.
    // OCR against it runs the same way it does for a pre-existing (edit-mode) image.
    const handleUrlSubmit = async (
        url: string,
        setUpload: React.Dispatch<React.SetStateAction<UploadState>>,
        errorKey?: string,
    ) => {
        if (errorKey) updateError(errorKey);
        
        // If it's already a Firebase URL, just use it directly to save time
        if (url.includes('firebasestorage.googleapis.com')) {
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
            return;
        }

        try {
            setUpload(prev => ({ ...prev, file: null, url: '', uploading: true, error: '', progress: 0 }));
            
            // Try to fetch the image and convert it to a File for Firebase
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            
            const fileName = "pasted_image" + (blob.type.includes('png') ? '.png' : '.jpg');
            const file = new File([blob], fileName, { type: blob.type });
            
            const pathSuffix = errorKey === 'chequeBackUpload' ? 'cheque_back' : 'proof';
            handleFileUpload(file, setUpload, pathSuffix, errorKey);
        } catch (err: any) {
            console.warn("Could not download pasted image for Firebase upload (likely CORS):", err);
            // Fallback: just use the URL directly if fetch fails
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
        }
    };

    const fetchPendingOrders = async () => {
        if (!user) return;
        setLoadingOrders(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getUserOrders(user.mobile), { params: { paymentStatus: 'PENDING_PAYMENT' } });
            const orders: PendingOrder[] = (response.data?.orders || response.data || []);
            setPendingOrders(orders);
        } catch { setPendingOrders([]); } finally { setLoadingOrders(false); }
    };

    const fetchFarms = async () => {
        setLoadingFarms(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getFarms());
            const data = response.data;
            const farmList = (data?.farms || data || []).map((f: any) => ({ id: f.id, location: f.location || f.address || f.name || f.id }));
            setFarms(farmList);
            if (farmList.length > 0 && !farmId) setFarmId(farmList[0].id);
        } catch { setFarms([]); } finally { setLoadingFarms(false); }
    };

    const fetchProducts = async () => {
        setLoadingProducts(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getProducts());
            const data = response.data;
            const productList = (data?.products || data || [])
                .filter((p: any) => p.inStock === true)
                .map((p: any) => ({
                    id: p.id, name: p.name || p.breed || p.id, price: parseFloat(p.price || 0) * 2,
                }));
            setProducts(productList);
            if (productList.length > 0 && !breedId) {
                setBreedId(productList[0].id);
                setBaseUnitCost(productList[0].price);
            }
        } catch { setProducts([]); } finally { setLoadingProducts(false); }
    };

    // ── Build transaction object ───────────────────────────────────────────────
    const buildTransaction = (orderTotal: number): TransactionEntry | undefined => {
        if (!paymentMethod || paymentMethod === 'NONE' || paymentMethod === 'COUPON') return undefined;

        const dateStr = txDate || undefined;

        if (paymentMethod === 'MULTIPLE') {
            if (multipleSubMode === 'COUPON') return undefined;
            const amount = Number(txAmount) || 0;
            const base: TransactionEntry = {
                paymentMethod: multipleSubMode as any,
                amount,
                transactionDate: dateStr,
                paid_by: paidBy,
                ...(comment.trim() ? { comment: comment.trim() } : {}),
            };
            if (multipleSubMode === 'BANK_TRANSFER') {
                return {
                    ...base,
                    transferMode,
                    ...(utrNumber.trim() ? { utrNumber } : {}),
                    ...(paidBy !== 'Self' && accountHolderName.trim() ? { accountHolderName } : {}),
                    ...(bankName.trim() ? { bankName } : {}),
                    paymentScreenshotUrl: screenshotUpload.url,
                };
            }
            if (multipleSubMode === 'CHEQUE') return { ...base, utrNumber, chequeFrontImage: chequeFrontUpload.url, chequeBackImage: chequeBackUpload.url };
            if (multipleSubMode === 'CASH') return { ...base, cashierName, cashierPhone, voucherImageUrl: voucherUpload.url };
            if (multipleSubMode === 'UPI') return { ...base, utrNumber, accountHolderName, paymentScreenshotUrl: upiScreenshotUpload.url };
            return base;
        }

        const amt = (txAmount !== '' && txAmount !== undefined && txAmount !== null) ? Number(txAmount) : orderTotal;
        const base: TransactionEntry = {
            paymentMethod: paymentMethod as any,
            amount: amt,
            transactionDate: dateStr,
            paid_by: paidBy,
            ...(comment.trim() ? { comment: comment.trim() } : {}),
        };
        if (paymentMethod === 'BANK_TRANSFER') {
            return {
                ...base,
                transferMode,
                ...(utrNumber.trim() ? { utrNumber } : {}),
                ...(paidBy !== 'Self' && accountHolderName.trim() ? { accountHolderName } : {}),
                ...(bankName.trim() ? { bankName } : {}),
                paymentScreenshotUrl: screenshotUpload.url,
            };
        }
        if (paymentMethod === 'CASH') return { ...base, cashierName, cashierPhone, voucherImageUrl: voucherUpload.url };
        if (paymentMethod === 'CHEQUE') return { ...base, utrNumber, chequeFrontImage: chequeFrontUpload.url, chequeBackImage: chequeBackUpload.url };
        if ((paymentMethod as string) === 'UPI') return { ...base, utrNumber, accountHolderName, paymentScreenshotUrl: upiScreenshotUpload.url };
        return base;
    };

    // ── Validate payment ───────────────────────────────────────────────────────
    const validatePayment = (orderTotal: number): Record<string, string> => {
        const errs: Record<string, string> = {};
        if (!paymentMethod) { errs.paymentMethod = 'Select a payment method'; }
        if (paymentMethod === 'NONE' || coinsFullyCover) return errs;

        const isCoupon = paymentMethod === 'COUPON' || (paymentMethod === 'MULTIPLE' && multipleSubMode === 'COUPON');
        if (!txDate && !isCoupon) {
            errs.txDate = 'Transaction date is required';
        } else if (txDate && !isCoupon && !isValidDdmmyyyy(txDate)) {
            errs.txDate = 'Enter date in DD-MM-YYYY format';
        }

        if (!isCoupon) {
            const amountVal = Number(txAmount) || 0;
            const effectiveRemaining = view === 'create_order' ? totalCost : remaining;

            if (!amountVal || amountVal <= 0) errs.txAmount = 'Enter payment amount';
            else if (amountVal > effectiveRemaining) errs.txAmount = `Amount exceeds remaining balance (₹${effectiveRemaining.toLocaleString('en-IN')})`;
        } else {
            if (!couponCode.trim()) errs.couponCode = 'Coupon code is required';
        }

        if (paymentMethod === 'MULTIPLE') {
            if (multipleSubMode === 'BANK_TRANSFER') {
                if (!screenshotUpload.url) errs.screenshotUpload = 'Payment screenshot is required';
                if (transferMode === 'CASH_DEPOSIT') {
                    if (utrNumber.trim()) { const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1; }
                    if (paidBy !== 'Self' && accountHolderName.trim()) {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    if (bankName.trim()) { const e3 = validateBankName(bankName); if (e3) errs.bankName = e3; }
                } else {
                    const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1;
                    if (paidBy !== 'Self') {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    const e3 = validateBankName(bankName); if (e3) errs.bankName = e3;
                }
            }
            if (multipleSubMode === 'CHEQUE') {
                const e = validateChequeNumber(utrNumber); if (e) errs.utrNumber = e;
                if (!chequeFrontUpload.url) errs.chequeFrontUpload = 'Cheque front image is required';
            }
            if (multipleSubMode === 'CASH') {
                if (!cashierName.trim()) errs.cashierName = 'Cashier name is required';
                if (cashierPhone.length !== 10) errs.cashierPhone = 'Enter a valid 10-digit mobile number';
                if (!voucherUpload.url) errs.voucherUpload = 'Voucher image is required';
            }
            if (multipleSubMode === 'UPI') {
                const e2 = validateUTR(utrNumber); if (e2) errs.utrNumber = e2;
                if (paidBy !== 'Self' && accountHolderName.trim()) {
                    const e3 = validateAccountHolderName(accountHolderName); if (e3) errs.accountHolderName = e3;
                }
                if (!upiScreenshotUpload.url) errs.upiScreenshotUpload = 'Payment screenshot is required';
            }
        } else {
            if (paymentMethod === 'BANK_TRANSFER') {
                if (!screenshotUpload.url) errs.screenshotUpload = 'Payment screenshot is required';
                if (transferMode === 'CASH_DEPOSIT') {
                    if (utrNumber.trim()) { const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1; }
                    if (paidBy !== 'Self' && accountHolderName.trim()) {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    if (bankName.trim()) { const e3 = validateBankName(bankName); if (e3) errs.bankName = e3; }
                } else {
                    const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1;
                    if (paidBy !== 'Self') {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    const e3 = validateBankName(bankName); if (e3) errs.bankName = e3;
                }
            }
            if (paymentMethod === 'CASH') {
                if (!cashierName.trim()) errs.cashierName = 'Cashier name is required';
                if (cashierPhone.length !== 10) errs.cashierPhone = 'Enter a valid 10-digit mobile number';
                if (!voucherUpload.url) errs.voucherUpload = 'Voucher image is required';
            }
            if (paymentMethod === 'CHEQUE') {
                const e = validateChequeNumber(utrNumber); if (e) errs.utrNumber = e;
                if (!chequeFrontUpload.url) errs.chequeFrontUpload = 'Cheque front image is required';
                // Cheque back image is optional
            }
            if (paymentMethod === 'UPI') {
                const e2 = validateUTR(utrNumber); if (e2) errs.utrNumber = e2;
                if (paidBy !== 'Self' && accountHolderName.trim()) {
                    const e3 = validateAccountHolderName(accountHolderName); if (e3) errs.accountHolderName = e3;
                }
                if (!upiScreenshotUpload.url) errs.upiScreenshotUpload = 'Payment screenshot is required';
            }
        }
        return errs;
    };

    // Reset sub-form fields only (keep uploads that already uploaded)
    const resetSubFormFields = () => {
        setUtrNumber(''); setAccountHolderName(''); setBankName('');
        setCashierName(''); setCashierPhone(''); setTxDate('');
        setScreenshotUpload(emptyUpload()); setVoucherUpload(emptyUpload());
        setChequeFrontUpload(emptyUpload()); setChequeBackUpload(emptyUpload());
        setUpiScreenshotUpload(emptyUpload());
        setCouponCode('');
        setComment('');
        setErrors({});
    };

    // ── Add Payment submit ─────────────────────────────────────────────────────
    const handleAddPayment = async (e: React.FormEvent) => {
        e.preventDefault();
        const orderTotal = selectedOrder?.totalCost || 0;
        const errs = validatePayment(orderTotal);
        setErrors(errs);
        if (Object.keys(errs).length > 0 || !selectedOrder || !user) return;
        setLoading(true);
        try {
            const isCoupon = paymentMethod === 'COUPON' || (paymentMethod === 'MULTIPLE' && multipleSubMode === 'COUPON');
            if (isCoupon) {
                const res = await couponService.applyCoupon({
                    code: couponCode.trim(),
                    order_id: selectedOrder.id
                }, adminMobile || '');

                if (res.error) {
                    throw new Error(res.error);
                }

                const appliedBy = (res.data as any)?.applied_by;
                setSnackbar({
                    message: appliedBy?.employee_name
                        ? `Coupon applied by ${appliedBy.employee_name} (${appliedBy.employee_role})`
                        : 'Coupon applied successfully!',
                    type: 'success',
                });

                if (paymentMethod === 'MULTIPLE') {
                    resetSubFormFields();
                    await fetchExistingTransactions(selectedOrder.id, orderTotal);
                } else {
                    setTimeout(() => { onSuccess(); onClose(); }, 1000);
                }
                return;
            }

            const transaction = buildTransaction(orderTotal);
            const payload: any = {
                orderId: selectedOrder.id,
                userId: user.mobile,
                transaction,
                paid_by: paidBy,
                paymentType: paymentMethod === 'MULTIPLE'
                    ? 'MULTIPLE'
                    : (paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT')
                        ? 'CASH_DEPOSIT'
                        : (['BANK_TRANSFER', 'CHEQUE', 'CASH', 'UPI'].includes(paymentMethod ?? '') && Number(txAmount) < orderTotal)
                            ? 'MULTIPLE'
                            : (paymentMethod || undefined),
                paymentUpdatedByMobile: adminMobile,
                paymentUpdatedByName: adminName,
                paymentUpdatedByRole: adminRole,
                paymentUpdatedDate: new Date().toISOString().split('T')[0],
                source: 'animalkart_dashboard',
                ...(comment.trim() ? { comment: comment.trim() } : {}),
            };
            const res = await api.post<any>(API_ENDPOINTS.confirmPayment(), payload);
            if (res.data?.statuscode >= 400) throw new Error(res.data?.message || 'Failed');

            resetSubFormFields();
            await fetchExistingTransactions(selectedOrder.id, orderTotal);
        } catch (err: any) {
            setSnackbar({ message: err.message || 'Failed to add payment', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // ── Create Order submit ────────────────────────────────────────────────────
    const handleCreateOrder = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        const errs: Record<string, string> = {};
        if (!breedId) errs.breedId = 'Please select a breed';
        if (!numUnits || numUnits < 1 || numUnits > 999) errs.numUnits = 'Enter units between 1 and 999';
        if (!farmId) errs.farmId = 'Please select a farm location';

        // Merge payment errors
        const paymentErrs = validatePayment(totalCost);
        Object.assign(errs, paymentErrs);

        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setLoading(true);
        try {
            const isCoupon = paymentMethod === 'COUPON';
            const payload: AdminCreateOrderRequest = {
                userId: user.mobile, breedId, numUnits, baseUnitCost,
                withCpf: false, cpfUnitCost: 0, farmId: farmId || undefined,
                paymentMode: coinsFullyCover ? 'COINS_REDEEM' : 'MANUAL_PAYMENT',
                // New API fields
                paymentUpdatedByMobile: adminMobile,
                paymentUpdatedByName: adminName,
                paymentUpdatedByRole: adminRole,
                orderCreatedDate: createdDate || undefined,
                paymentType: (coinsFullyCover || effectiveCoinsRedeemed > 0 || paymentMethod === 'NONE' || isCoupon)
                    ? undefined
                    : (paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT')
                        ? 'CASH_DEPOSIT'
                        : (paymentMethod || undefined),
                coinsRedeemed: coinsFullyCover ? totalCost : 0,
                transactions: (coinsFullyCover || isCoupon) ? undefined : [buildTransaction(totalCost)!],
                paid_by: paidBy,
                comment: comment.trim() || undefined,
            } as any;
            const result = await adminOrderService.createOrder(payload, adminMobile);
            if (result.error) {
                setSnackbar({ message: result.error, type: 'error' });
                return;
            }

            if (isCoupon) {
                const createdOrderId = result.data?.id || result.data?.order?.id || result.data?.orderId;
                if (!createdOrderId) {
                    setSnackbar({ message: 'Order created, but failed to find order ID to apply coupon.', type: 'error' });
                    return;
                }

                const couponRes = await couponService.applyCoupon({
                    code: couponCode.trim(),
                    order_id: createdOrderId
                }, adminMobile || '');

                if (couponRes.error) {
                    setSnackbar({ message: `Order created, but failed to apply coupon: ${couponRes.error}`, type: 'error' });
                    return;
                }
            }

            setSnackbar({ message: 'Order created successfully!', type: 'success' });
            setTimeout(() => { onSuccess(); onClose(); }, 800);
        } catch (err: any) {
            setSnackbar({ message: err.message || 'Failed to create order', type: 'error' });
        } finally { setLoading(false); }
    };


    if (!isOpen) return null;

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-hidden font-sans">

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                        <div>
                            <h3 className="text-base font-bold text-slate-800">
                                {view === 'list' ? 'Orders' : view === 'add_payment' ? 'Add Payment' : 'Create New Order'}
                            </h3>
                            <p className="text-xs text-slate-400 mt-0.5">{userName} · <MaskedMobile mobile={user?.mobile} countryCode={user?.country_code} className="text-xs text-slate-400" /></p>
                        </div>
                        <div className="flex items-center gap-3">
                            {view !== 'list' && (
                                <nav className="flex items-center gap-1.5 text-sm">
                                    <button
                                        onClick={() => { setView('list'); resetPaymentState(); setSelectedOrder(null); }}
                                        className="text-slate-500 hover:text-slate-800 font-medium transition-colors"
                                    >
                                        Orders
                                    </button>
                                    <span className="text-slate-300 select-none">›</span>
                                    <span className="text-slate-800 font-semibold">
                                        {view === 'add_payment' ? 'Add Payment' : 'Create Order'}
                                    </span>
                                </nav>
                            )}
                            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6 overflow-y-auto space-y-5">

                        {/* ── LIST VIEW ── */}
                        {view === 'list' && (
                            <>
                                <div>
                                    <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                        <Clock size={12} /> Pending Orders
                                    </h4>
                                    {loadingOrders ? (
                                        <div className="space-y-3">{[1, 2].map(i => <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />)}</div>
                                    ) : pendingOrders.length === 0 ? (
                                        <div className="text-xs text-slate-400 py-3 text-center bg-slate-50 rounded-xl">No pending orders</div>
                                    ) : (
                                        <div className="space-y-3">
                                            <p className="text-xs text-slate-500 font-medium">{pendingOrders.length} Order{pendingOrders.length !== 1 ? 's' : ''}</p>
                                            {pendingOrders.map(order => (
                                                <div key={order.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                                                    {/* Header */}
                                                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
                                                        <div>
                                                            <p className="text-xs font-bold text-slate-800">Order ID : {order.id}</p>
                                                            <p className="text-[10px] text-slate-400 mt-0.5">
                                                                Placed on : {order.placedAt ? formatRawDateTime(order.placedAt) : '—'}
                                                            </p>
                                                        </div>
                                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-400 text-white">Pending Pay</span>
                                                    </div>

                                                    {/* Body */}
                                                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                                                        <div className="space-y-0.5">
                                                            <p className="text-xs font-bold text-slate-700">BREED ID: {order.breedId || '—'}</p>
                                                            <p className="text-[10px] text-slate-500">{order.buffaloCount || 0} buffaloes</p>
                                                            <p className="text-[10px] text-slate-500">{order.calfCount || 0} calves</p>
                                                            <p className="text-[10px] text-slate-500">{order.numUnits || 1} Unit{(order.numUnits || 1) > 1 ? 's' : ''}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-[10px] text-slate-400 font-medium">Total</p>
                                                            <p className="text-sm font-bold text-slate-800">₹{(order.totalCost || 0).toLocaleString('en-IN')}</p>
                                                            {(order.coinsRedeemed ?? 0) > 0 && (
                                                                <p className="text-[10px] text-amber-500 font-semibold mt-0.5">🪙 Redeemed {(order.coinsRedeemed || 0).toLocaleString('en-IN')}</p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Payment Progress */}
                                                    {(order.paidAmount ?? 0) > 0 && (
                                                        <div className="px-4 py-3 border-b border-slate-100 bg-green-50">
                                                            <div className="flex items-center justify-between mb-1.5">
                                                                <span className="text-xs font-bold text-green-700">Payment Progress</span>
                                                                <span className="text-xs font-bold text-green-700">
                                                                    {Math.round(((order.paidAmount ?? 0) / (order.totalCost || 1)) * 100)}%
                                                                </span>
                                                            </div>
                                                            <div className="w-full h-1.5 bg-green-200 rounded-full overflow-hidden mb-2">
                                                                <div
                                                                    className="h-full bg-green-500 rounded-full transition-all"
                                                                    style={{ width: `${Math.min(((order.paidAmount ?? 0) / (order.totalCost || 1)) * 100, 100)}%` }}
                                                                />
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <div>
                                                                    <p className="text-[10px] text-green-600 font-semibold flex items-center gap-1">✓ Paid (Under Review)</p>
                                                                    <p className="text-xs font-bold text-green-700">₹{(order.paidAmount ?? 0).toLocaleString('en-IN')}</p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-[10px] text-amber-600 font-semibold">⏱ Remaining</p>
                                                                    <p className="text-xs font-bold text-amber-600">₹{(order.pendingAmount ?? 0).toLocaleString('en-IN')}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Footer */}
                                                    <div className="px-4 py-2.5 flex items-center justify-between gap-2">
                                                        <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                                                            {order.paymentType === 'MANUAL_PAYMENT' ? 'Manual Payment' : (order.paymentType || 'Manual Payment')}
                                                        </span>
                                                        <button
                                                            onClick={() => { setSelectedOrder(order); resetPaymentState(); setView('add_payment'); }}
                                                            className="text-xs font-semibold text-white bg-[#1B4332] hover:bg-[#163829] px-3 py-1.5 rounded-lg transition-all"
                                                        >
                                                            Update Payment
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                            </>
                        )}

                        {/* ── ADD PAYMENT VIEW ── */}
                        {view === 'add_payment' && selectedOrder && (
                            <form onSubmit={handleAddPayment} className="space-y-4">
                                {/* Order summary */}
                                <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                                    <p className="text-xs text-blue-600 font-semibold">Order</p>
                                    <p className="text-sm font-bold text-blue-800">{selectedOrder.id}</p>
                                    <p className="text-xs text-blue-600">Total: ₹{(selectedOrder.totalCost || 0).toLocaleString('en-IN')}</p>
                                </div>

                                {/* Existing transactions */}
                                <ExistingTransactionsList
                                    transactions={existingTransactions}
                                    totalCost={selectedOrder.totalCost || 0}
                                    totalPaid={totalPaid}
                                    remaining={remaining}
                                    loading={isLoadingTransactions}
                                    onViewImage={url => setPreviewUrl({ url })}
                                />

                                {/* Payment form */}
                                <PaymentForm
                                    showMultiple={true}
                                    forceMultiple={selectedOrder.paymentType === 'MULTIPLE'}
                                    orderTotal={selectedOrder.totalCost || 0}
                                    remaining={remaining}
                                    paymentMethod={paymentMethod}
                                    setPaymentMethod={setPaymentMethod}
                                    txDate={txDate} setTxDate={setTxDate}
                                    transferMode={transferMode} setTransferMode={setTransferMode}
                                    utrNumber={utrNumber} setUtrNumber={setUtrNumber}
                                    accountHolderName={accountHolderName} setAccountHolderName={setAccountHolderName}
                                    bankName={bankName} setBankName={setBankName}
                                    screenshotUpload={screenshotUpload} onScreenshotChange={f => handleFileUpload(f, setScreenshotUpload, 'screenshot', 'screenshotUpload')}
                                    onScreenshotUrlSubmit={url => handleUrlSubmit(url, setScreenshotUpload, 'screenshotUpload')}
                                    onScreenshotClear={() => setScreenshotUpload(emptyUpload())}
                                    cashierName={cashierName} setCashierName={setCashierName}
                                    cashierPhone={cashierPhone} setCashierPhone={setCashierPhone}
                                    voucherUpload={voucherUpload} onVoucherChange={f => handleFileUpload(f, setVoucherUpload, 'voucher', 'voucherUpload')}
                                    onVoucherUrlSubmit={url => handleUrlSubmit(url, setVoucherUpload, 'voucherUpload')}
                                    onVoucherClear={() => setVoucherUpload(emptyUpload())}
                                    chequeFrontUpload={chequeFrontUpload} onChequeFrontChange={f => handleFileUpload(f, setChequeFrontUpload, 'cheque_front', 'chequeFrontUpload')}
                                    onChequeFrontUrlSubmit={url => handleUrlSubmit(url, setChequeFrontUpload, 'chequeFrontUpload')}
                                    onChequeFrontClear={() => setChequeFrontUpload(emptyUpload())}
                                    chequeBackUpload={chequeBackUpload} onChequeBackChange={f => handleFileUpload(f, setChequeBackUpload, 'cheque_back', 'chequeBackUpload')}
                                    onChequeBackUrlSubmit={url => handleUrlSubmit(url, setChequeBackUpload, 'chequeBackUpload')}
                                    onChequeBackClear={() => setChequeBackUpload(emptyUpload())}
                                    multipleSubMode={multipleSubMode} setMultipleSubMode={setMultipleSubMode}
                                    upiScreenshotUpload={upiScreenshotUpload} onUpiScreenshotChange={f => handleFileUpload(f, setUpiScreenshotUpload, 'upi_screenshot', 'upiScreenshotUpload')}
                                    onUpiScreenshotUrlSubmit={url => handleUrlSubmit(url, setUpiScreenshotUpload, 'upiScreenshotUpload')}
                                    onUpiScreenshotClear={() => setUpiScreenshotUpload(emptyUpload())}
                                    txAmount={txAmount} setTxAmount={setTxAmount}
                                    onView={(url, text, loading) => setPreviewUrl({ url, text, loading })}
                                    onReupload={() => clearAutoFilledFields({ setUtrNumber, setBankName, setTxAmount, setTxDate, setTransferMode })}
                                    paidBy={paidBy} setPaidBy={setPaidBy}
                                    couponCode={couponCode} setCouponCode={setCouponCode}
                                    comment={comment} setComment={setComment}
                                    mobile={user?.mobile}
                                    errors={errors}
                                />
                                {errors.paymentMethod && <p className="text-[10px] text-red-500">{errors.paymentMethod}</p>}

                                <div className="flex gap-3 pt-2 border-t border-slate-100">
                                    <button type="button" onClick={() => setView('list')}
                                        className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold text-sm">
                                        Back
                                    </button>
                                    <button type="submit" disabled={loading || anyUploading}
                                        className="flex-1 px-4 py-2.5 bg-[#1B4332] text-white rounded-xl hover:bg-[#163829] font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                        <CreditCard size={15} />
                                        {loading ? 'Saving...' : anyUploading ? 'Uploading...' : 'Add Payment'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* ── CREATE ORDER VIEW ── */}
                        {view === 'create_order' && (
                            <form onSubmit={handleCreateOrder} className="space-y-5">
                                <div>
                                    <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-3">Order Details</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1 col-span-2">
                                            <label className="block text-xs font-medium text-slate-600">Breed <span className="text-red-500">*</span></label>
                                            {loadingProducts ? <div className="h-9 bg-slate-100 rounded-lg animate-pulse" /> : (
                                                <Select
                                                    value={breedId}
                                                    onChange={(v) => { setBreedId(v); const p = products.find(x => x.id === v); if (p) setBaseUnitCost(p.price); updateError('breedId'); }}
                                                    options={products.map(p => ({ value: p.id, label: `${p.name} — ₹${p.price.toLocaleString('en-IN')} / unit` }))}
                                                    className={`w-full pl-3 py-2 rounded-lg border text-sm bg-white ${errors.breedId ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'}`}
                                                />
                                            )}
                                            {errors.breedId && <p className="text-[10px] text-red-500">{errors.breedId}</p>}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="block text-xs font-medium text-slate-600">Number of Units <span className="text-red-500">*</span></label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={999}
                                                step={1}
                                                value={numUnits || ''}
                                                onKeyDown={(e) => {
                                                    if (['.', '-', '+', 'e', 'E'].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    if (val === '') {
                                                        setNumUnits('' as any);
                                                    } else {
                                                        const parsed = Math.min(999, Math.max(1, parseInt(val, 10)));
                                                        setNumUnits(parsed);
                                                    }
                                                    updateError('numUnits');
                                                }}
                                                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${errors.numUnits ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'}`}
                                            />
                                            {errors.numUnits && <p className="text-[10px] text-red-500">{errors.numUnits}</p>}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="block text-xs font-medium text-slate-600">Farm Location <span className="text-red-500">*</span></label>
                                            {loadingFarms ? <div className="h-9 bg-slate-100 rounded-lg animate-pulse" /> : (
                                                <Select
                                                    value={farmId}
                                                    onChange={(v) => { setFarmId(v); setErrors(prev => { const n = { ...prev }; delete n.farmId; return n; }); }}
                                                    options={[
                                                        { value: '', label: 'Select farm' },
                                                        ...farms.map(f => ({ value: f.id, label: f.location || f.id })),
                                                    ]}
                                                    className={`w-full px-3 py-2 rounded-lg border text-sm ${errors.farmId ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'}`}
                                                />
                                            )}
                                            {errors.farmId && <p className="text-[10px] text-red-500">{errors.farmId}</p>}
                                        </div>
                                        {breedId && (
                                            <div className="col-span-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex justify-between items-center">
                                                <span className="text-sm font-medium text-blue-700 flex items-center gap-1"><IndianRupee size={14} /> Total Order Amount</span>
                                                <span className="text-lg font-bold text-blue-800">₹{totalCost.toLocaleString('en-IN')}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Created by */}
                                <div className="border-t border-slate-100 pt-4">
                                    <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-3">Created By</h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-1">
                                            <label className="block text-xs font-medium text-slate-600">Name</label>
                                            <input type="text" value={adminName} readOnly className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500" />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="block text-xs font-medium text-slate-600">Mobile</label>
                                            <input type="text" value={adminMobile} readOnly className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-black" />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="block text-xs font-medium text-slate-600">Created Date</label>
                                            <input type="text" value={isoToDdmmyyyy(createdDate)} readOnly
                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500 cursor-default" />
                                        </div>
                                    </div>
                                </div>

                                {/* Coins Redeem */}
                                {!loadingCoins && availableCoins !== null && availableCoins > 0 && <div className="border-t border-slate-100 pt-4">
                                    <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-3">Coins Redeem</h4>
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-3">
                                        {/* Available coins row */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                                                <span className="text-base">🪙</span> Available Coins
                                            </span>
                                            {loadingCoins ? (
                                                <span className="text-xs text-amber-600 animate-pulse">Loading...</span>
                                            ) : (
                                                <span className="text-sm font-bold text-amber-700">
                                                    {availableCoins !== null ? availableCoins.toLocaleString('en-IN') : '—'}
                                                </span>
                                            )}
                                        </div>

                                        {/* Redeem toggle */}
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="redeemCoinsToggle"
                                                checked={redeemCoins}
                                                onChange={e => {
                                                    setRedeemCoins(e.target.checked);
                                                    if (!e.target.checked) { setCoinRedeemMode(null); setCoinsToRedeem(''); }
                                                }}
                                                className="w-4 h-4 accent-amber-500 cursor-pointer"
                                            />
                                            <label htmlFor="redeemCoinsToggle" className="text-xs font-semibold text-amber-700 cursor-pointer">
                                                Redeem coins for this order
                                            </label>
                                        </div>

                                        {/* Redeem mode selection */}
                                        {redeemCoins && (
                                            <div className="space-y-3">
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setCoinRedeemMode('all'); setCoinsToRedeem(''); }}
                                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition-all ${coinRedeemMode === 'all' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:border-amber-400'}`}
                                                    >
                                                        Redeem All ({availableCoins !== null ? Math.min(availableCoins, totalCost).toLocaleString('en-IN') : '—'})
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCoinRedeemMode('custom')}
                                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition-all ${coinRedeemMode === 'custom' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:border-amber-400'}`}
                                                    >
                                                        Custom
                                                    </button>
                                                </div>

                                                {/* Custom input */}
                                                {coinRedeemMode === 'custom' && (
                                                    <div className="space-y-1">
                                                        <label className="block text-xs font-medium text-slate-600">Coins to Redeem <span className="text-red-500">*</span></label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            max={availableCoins ?? undefined}
                                                            value={coinsToRedeem}
                                                            onWheel={e => (e.target as HTMLInputElement).blur()}
                                                            onKeyDown={e => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                if (availableCoins !== null && Number(val) > availableCoins) {
                                                                    setCoinsToRedeem(String(availableCoins));
                                                                } else {
                                                                    setCoinsToRedeem(val);
                                                                }
                                                            }}
                                                            placeholder="Enter coins to redeem"
                                                            className="w-full px-3 py-2 rounded-lg border border-amber-300 text-sm outline-none focus:border-amber-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                )}

                                                {/* Summary */}
                                                {effectiveCoinsRedeemed > 0 && (
                                                    <div className={`rounded-lg px-3 py-2 text-xs font-medium space-y-1 ${coinsFullyCover ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-blue-50 border border-blue-100 text-blue-700'}`}>
                                                        <div className="flex justify-between">
                                                            <span>Coins redeemed:</span>
                                                            <span className="font-bold">{effectiveCoinsRedeemed.toLocaleString('en-IN')}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>}

                                {/* Payment (optional, no MULTIPLE in create_order) */}
                                {!coinsFullyCover && (
                                    <div className="border-t border-slate-100 pt-4">
                                        <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-3">
                                            Payment Details
                                        </h4>
                                        <PaymentForm
                                            showMultiple={true}
                                            showCoupon={false}
                                            orderTotal={remainingAfterCoins || totalCost}
                                            remaining={remainingAfterCoins || totalCost}
                                            paymentMethod={paymentMethod}
                                            setPaymentMethod={setPaymentMethod}
                                            txDate={txDate} setTxDate={setTxDate}
                                            transferMode={transferMode} setTransferMode={setTransferMode}
                                            utrNumber={utrNumber} setUtrNumber={setUtrNumber}
                                            accountHolderName={accountHolderName} setAccountHolderName={setAccountHolderName}
                                            bankName={bankName} setBankName={setBankName}
                                            screenshotUpload={screenshotUpload} onScreenshotChange={f => handleFileUpload(f, setScreenshotUpload, 'screenshot', 'screenshotUpload')}
                                            onScreenshotUrlSubmit={url => handleUrlSubmit(url, setScreenshotUpload, 'screenshotUpload')}
                                            onScreenshotClear={() => setScreenshotUpload(emptyUpload())}
                                            cashierName={cashierName} setCashierName={setCashierName}
                                            cashierPhone={cashierPhone} setCashierPhone={setCashierPhone}
                                            voucherUpload={voucherUpload} onVoucherChange={f => handleFileUpload(f, setVoucherUpload, 'voucher', 'voucherUpload')}
                                            onVoucherUrlSubmit={url => handleUrlSubmit(url, setVoucherUpload, 'voucherUpload')}
                                            onVoucherClear={() => setVoucherUpload(emptyUpload())}
                                            chequeFrontUpload={chequeFrontUpload} onChequeFrontChange={f => handleFileUpload(f, setChequeFrontUpload, 'cheque_front', 'chequeFrontUpload')}
                                            onChequeFrontUrlSubmit={url => handleUrlSubmit(url, setChequeFrontUpload, 'chequeFrontUpload')}
                                            onChequeFrontClear={() => setChequeFrontUpload(emptyUpload())}
                                            chequeBackUpload={chequeBackUpload} onChequeBackChange={f => handleFileUpload(f, setChequeBackUpload, 'cheque_back', 'chequeBackUpload')}
                                            onChequeBackUrlSubmit={url => handleUrlSubmit(url, setChequeBackUpload, 'chequeBackUpload')}
                                            onChequeBackClear={() => setChequeBackUpload(emptyUpload())}
                                            multipleSubMode={multipleSubMode} setMultipleSubMode={setMultipleSubMode}
                                            upiScreenshotUpload={upiScreenshotUpload} onUpiScreenshotChange={f => handleFileUpload(f, setUpiScreenshotUpload, 'upi_screenshot', 'upiScreenshotUpload')}
                                            onUpiScreenshotUrlSubmit={url => handleUrlSubmit(url, setUpiScreenshotUpload, 'upiScreenshotUpload')}
                                            onUpiScreenshotClear={() => setUpiScreenshotUpload(emptyUpload())}
                                            txAmount={txAmount} setTxAmount={setTxAmount}
                                            onView={(url, text, loading) => setPreviewUrl({ url, text, loading })}
                                            onReupload={() => clearAutoFilledFields({ setUtrNumber, setBankName, setTxAmount, setTxDate, setTransferMode })}
                                            paidBy={paidBy} setPaidBy={setPaidBy}
                                            couponCode={couponCode} setCouponCode={setCouponCode}
                                            comment={comment} setComment={setComment}
                                            mobile={user?.mobile}
                                            errors={errors}
                                        />
                                    </div>
                                )}

                                <div className="flex gap-3 pt-2 border-t border-slate-100">
                                    <button type="button" onClick={onClose}
                                        className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-semibold text-sm transition-colors">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={loading || anyUploading}
                                        className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors shadow-sm">
                                        {loading ? <><Loader2 size={15} className="animate-spin" /> Creating...</> : anyUploading ? <><Loader2 size={15} className="animate-spin" /> Uploading...</> : <><ShoppingCart size={15} /> Create Order</>}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Sticky Create New Order button — list view only */}
                    {view === 'list' && (
                        <div className="shrink-0 px-6 py-4 border-t border-slate-100 bg-white">
                            <button onClick={() => { resetPaymentState(); resetOrderState(); setView('create_order'); }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold text-sm">
                                <PlusCircle size={16} /> Create New Order
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ImagePreviewModal
                url={previewUrl?.url ?? null} text={previewUrl?.text} loading={previewUrl?.loading}
                onClose={() => setPreviewUrl(null)}
                activePaymentType={paymentMethod === 'MULTIPLE' ? multipleSubMode : paymentMethod}
                remaining={remaining}
                onAutoFill={values => applyAutoFillValues(
                    values,
                    { setTxAmount, setTxDate, setTransferMode, setPaidBy, setUtrNumber, setAccountHolderName, setBankName, setCashierName, setCashierPhone },
                )}
            />
            {snackbar && <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />}
        </>
    );
};
